import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Tooltip } from '@mui/material';

import {
  ArrowUp,
  Box,
  Check,
  ChevronRight as ChevronRightSmall,
  Copy,
  Crosshair,
  Download,
  Eye,
  Lightbulb,
  Loader2,
  Pencil,
  Trash2,
  X,
  Zap,
} from 'lucide-react';

import Autoposition from '../../../assets/Autoposition.svg?react';
import AutopositionIcon from '../../../assets/AutopositionIcon.svg?react';
import ExportGreen from '../../../assets/ExportGreen.svg?react';
import ExportSvg from '../../../assets/ExportSvg.svg?react';
import TamanLogo from '../../../assets/TamanLogo.svg?react';
import MarketMatchToolbar from '../../../assets/toolbar_icons/MarketMatchToolbar.svg?react';
import { LAYER_NORM_CUSTOM, LAYER_NORM_NONE, NORMS } from '../../../constants/layers.ts';
import { usePdfExport } from '../../../context/PdfExportContext.tsx';
import { useViewModeTransition } from '../../../context/ViewModeTransitionContext.tsx';
import { useExport } from '../../../context/useExport';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { usePdfPhotometrySelectionEnabled, usePedestrianLayerTypeEnabled } from '../../../hooks/useIdlc1129Features';
import i18n from '../../../i18n';
import { useStore } from '../../../pages/workspace/hooks.ts';
import { httpGET } from '../../../services/http';
import { toastReducer } from '../../../store/reducers/toast.reducer';
import { userReducer } from '../../../store/reducers/user.reducer';
import {
  createVersionFromMarketMatch,
  downloadBlenderProject,
  downloadIesFile,
  loadLampSimilarityDetails,
  setExpandedSections,
  startLampSimilarity,
  updateLampProductVariant,
  workspaceReducer,
} from '../../../store/reducers/workspace.reducer.ts';
import { MeasurementData } from '../../../store/reducers/workspace.reducer.ts';
import { getUserFeatureFlags } from '../../../store/selectors/featureFlags.selector';
import { getNorms, getUser } from '../../../store/selectors/user.selector';
import { greyP2, greyP4, greyP5, orangeError } from '../../../theme.ts';
import { MeshGroup } from '../../../types/mesh.ts';
import { ModelLayer } from '../../../types/model.ts';
import { RankedCandidate } from '../../../types/project.ts';
import { MEASUREMENT_VISUALIZATION_LABELS } from '../../../types/view';
import { fetchCatalogueProduct } from '../../../utils/catalogueProductCache';
import { buildCatalogueUrl } from '../../../utils/catalogueUrl';
import {
  FRONTEND_PDF_PLOTS_FLAG,
  MARKET_MATCH_TO_VERSION_FLAG,
  MEASUREMENT_SURFACES_FLAG,
} from '../../../utils/featureFlagKeys';
import {
  canExportPdf,
  isAutopositioningInProgress as checkIsAutopositioningInProgress,
  isCalculationCompleted as isCalculationCompletedHelper,
  isCalculationInProgress as isCalculationInProgressHelper,
  isCalculationStarted,
  isJobCompleted,
  isJobFailed,
  isJobInProgress,
  isRenderBakingInProgress as isRenderBakingInProgressHelper,
} from '../../../utils/jobStatus.ts';
import { isFullyCatalogueAssigned, isMarketMatchedVersion } from '../../../utils/lampMarketMatch';
import { layerTypeKeys, layerTypeLabel } from '../../../utils/layerTypeLabel';
import {
  canApplyMarketMatchCandidate,
  getMarketMatchPreviewMode,
  selectWorkspaceMeasurementData,
} from '../../../utils/measurementCalculation/measurementSource';
import {
  ExpectedActualPair,
  getActualValueColor,
  getComparisonOperator,
  getTargetsSatisfiedCount,
  getTargetsSatisfiedCountFromAggregateResults,
  isTargetSatisfied,
  measurementDataToAggregateResults,
} from '../../../utils/normSatisfactionUtils.ts';
import { PDF_PHOTOMETRY_ENTRIES, isPhotometryIncluded } from '../../../utils/pdfPlotGeneration/photometryMapping';
import { createLayerTitle, getIlluminatedLayers, getNormName } from '../../../utils/pdfUtils';
import { getPhotometricSymbol } from '../../../utils/photometricSymbols.tsx';
import {
  fromDisplayLength,
  getLengthInputStep,
  getLengthUnitLabel,
  getPhotometricUnit,
  toDisplayLength,
} from '../../../utils/unitSystem.ts';
import IesPreview3DModal from '../../catalogue/IesPreview3DModal';
import { MeshGroupTree } from '../../common/MeshGroupTree';
import { NumericInput } from '../../common/NumericInput';
import CircularProgress from '../../loading/CircularProgress';
import { CustomTargetsForm } from '../../photometric-targets/CustomTargetsForm';
import { LightingStandardsInfo } from '../../project-settings/components/LayerSidebar/LightingStandardsInfo';
import { LayerTypeIcon } from '../../project-settings/components/LayerTypeIcon';
import { LAYER_TYPES } from '../../project-settings/types';
import { MaxAngleControl } from '../MaxAngleControl';
import LayersIcon from '../icons/LayersIcon';
import LightingCustom from '../icons/LightingCustom';
import LightingIcon from '../icons/LightingIcon';
import LightingRender from '../icons/LightingRender';
import RasterIcon from '../icons/RasterIcon';
import ResultsIcon from '../icons/ResultsIcon';
import { ProductVariantPicker } from '../scene/ProductVariantPicker.tsx';
import type { ProductVariantSelection } from '../scene/ProductVariantPicker.tsx';
import { probeWebGpuSupport } from '../scene/realtime/webgpuSupport.ts';
import { AddLuminaireButton } from './AddLuminaireButton';
import { MarketMatchPrefiltersPanel } from './MarketMatchPrefiltersPanel';
import { MarketMatchResultsPreviewModal } from './MarketMatchResultsPreviewModal';
import { MeasurementSurfacesPanel } from './MeasurementSurfacesPanel.tsx';

const DISTANCE_TO_GROUND_NA = -1;

interface SidebarProps {
  isOpen: boolean;
  showExportsPanel: boolean;
  onExportsPanelChange: (show: boolean) => void;
}

export function Sidebar({ isOpen, showExportsPanel, onExportsPanelChange }: SidebarProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const norms = useSelector(getNorms);
  const user = useSelector(getUser)!;
  const unitSystem = user.settings.units;
  const isCancelingAutoPositioning = useAppSelector((state) => state.workspace.isCancelingAutoPositioning);
  const lampSimilarityDetails = useAppSelector((state) => state.workspace.lampSimilarityDetails);
  const marketMatchToVersionEnabled = useAppSelector((state) =>
    state.featureFlags.userFeatureFlags.includes(MARKET_MATCH_TO_VERSION_FLAG),
  );
  const userFeatureFlags = useAppSelector(getUserFeatureFlags);
  const measurementSurfacesEnabled = useAppSelector((state) =>
    state.featureFlags.userFeatureFlags.includes(MEASUREMENT_SURFACES_FLAG),
  );
  const isLoadingLampSimilarityDetails = useAppSelector((state) => state.workspace.isLoadingLampSimilarityDetails);
  const appliedLampSimilaritySelection = useAppSelector((state) => state.workspace.appliedLampSimilaritySelection);
  const isCreatingVersion = useAppSelector((state) => state.workspace.isCreatingVersion);
  const normsLoadedRef = useRef(false);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [editingDisplayName, setEditingDisplayName] = useState<string>('');
  const [editingLampId, setEditingLampId] = useState<string | null>(null);
  const [editingLampName, setEditingLampName] = useState<string>('');
  const [expandedNormLayer, setExpandedNormLayer] = useState<string | null>(null);
  const [expandedTypeLayer, setExpandedTypeLayer] = useState<string | null>(null);
  const pdfPhotometrySelection = usePdfPhotometrySelectionEnabled();
  const pedestrianLayerType = usePedestrianLayerTypeEnabled();
  const [typeDropdownPosition, setTypeDropdownPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [layerSearchQuery, setLayerSearchQuery] = useState('');
  const [measurementSearchQuery, setMeasurementSearchQuery] = useState('');
  const [luminaireHeightInput, setLuminaireHeightInput] = useState('8');
  const [marketMatchResultsPreview, setMarketMatchResultsPreview] = useState<{
    lampName: string;
    aggregateResults: NonNullable<RankedCandidate['aggregateResults']>;
  } | null>(null);

  // Lamp ID for which the product variant picker is open (sidebar-initiated).
  const [productPickerLampId, setProductPickerLampId] = useState<string | null>(null);

  // Resolved "Product · Variant" labels keyed by `${productId}-${variantId}`.
  const [catalogueLabels, setCatalogueLabels] = useState<Record<string, string>>({});

  const [ies3dPreview, setIes3dPreview] = useState<{
    isOpen: boolean;
    iesUrl?: string;
    lampName: string;
  }>({ isOpen: false, lampName: '' });
  const latestIesPreviewRequestIdRef = useRef(0);

  // Layer sorting state
  const [layerSortKey, setLayerSortKey] = useState<'name' | 'type' | 'chl' | 'lmn' | null>('name');
  const [layerSortAsc, setLayerSortAsc] = useState(true);

  const handleLayerSort = (key: 'name' | 'type' | 'chl' | 'lmn') => {
    if (layerSortKey === key) {
      setLayerSortAsc(!layerSortAsc);
    } else {
      setLayerSortKey(key);
      setLayerSortAsc(true);
    }
  };

  // Refs for auto-scroll containers
  const layersSectionRef = useRef<HTMLDivElement>(null);
  const layerListRef = useRef<HTMLDivElement>(null);
  const lampListRef = useRef<HTMLDivElement>(null);
  const prevSelectedLayerRef = useRef<string | null>(null);
  const prevSelectedLampIdRef = useRef<string | null>(null);
  // Tracks which version we've already applied "default sections expansion"
  // to, so the init effect fires exactly once per version (on entry / on
  // version switch) and doesn't fight the user's manual toggles afterwards.
  const sectionsInitForVersionRef = useRef<number | null>(null);

  // Highlight toggles effect (flashing orange borders on CHL/Lmn toggles)
  const [highlightToggles, setHighlightToggles] = useState(false);
  const [highlightHeights, setHighlightHeights] = useState(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Toolbar settings popup state

  const {
    selectedMeasurements,
    handleMeasurementCheck,
    checkedItems,
    handleCheck,
    selectedPhotometries,
    handlePhotometryCheck,
    numPages,
  } = useExport();

  const {
    hoveredLayer,
    setHoveredLayer,
    lamps,
    addLamp,
    removeLamp,
    updateLampPosition,
    modelLayers,
    toggleLayerIllumination,
    toggleLayerLuminaireHolder,
    selectLamp,
    updateLayerType,
    updateLayerCustomTargets,
    updateLayerNorm,
    updateLayerDisplayName,
    updateLampDistance,
    selectedLayer,
    setSelectedLayer,
    expandedSections,
    toggleSection,
    openSection,
    startTamanOptimization,
    project,
    activeVersion,
    handleLayerClick,
    setShowAutoPositionSidebar,
    autoPositionLights,
    cancelAutoPositioning,
    desiredLuminaireHeight,
    setDesiredLuminaireHeight,
    setModelLayers,
    updateLampName,
    activeViewModes,
    setTargetCameraPosition,
    activeToolbarTool,
    setActiveToolbarTool,
    clearMeasurementPoints,
    clearLampDistanceSelection,
    meshGroups,
    uploadedModels,
    updateLayerGroupId,
    addMeshGroup,
    renameMeshGroup,
    deleteMeshGroup,
    isOptimizationStarting,
    isAutoPositioningStarting,
  } = useStore();

  // Identity: at least one lamp has a catalogue assignment → show product-picker UI etc.
  const isMarketMatched = isMarketMatchedVersion(activeVersion);
  // Capability: ALL lamps are catalogue-assigned → frontend engine can cover the whole scene.
  const fullyAssigned = isFullyCatalogueAssigned(activeVersion);
  const measurementData = useAppSelector(selectWorkspaceMeasurementData);
  const frontendPdfPlotsEnabled = useAppSelector((state) =>
    getUserFeatureFlags(state).includes(FRONTEND_PDF_PLOTS_FLAG),
  );
  const lengthUnitLabel = getLengthUnitLabel(unitSystem);
  const heightInputStep = getLengthInputStep(unitSystem);
  const MIN_LUMINAIRE_HEIGHT_METERS = 2;
  const MAX_LUMINAIRE_HEIGHT_METERS = 25;
  const heightInputMin = toDisplayLength(MIN_LUMINAIRE_HEIGHT_METERS, unitSystem);
  const heightInputMax = toDisplayLength(MAX_LUMINAIRE_HEIGHT_METERS, unitSystem);
  const isMaxLampsReached = lamps.length >= 50;

  // Re-sync the displayed input only when the unit system changes; we intentionally do not
  // depend on `desiredLuminaireHeight` so user typing isn't reformatted mid-edit.
  useEffect(() => {
    setLuminaireHeightInput(toDisplayLength(desiredLuminaireHeight ?? 8, unitSystem).toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSystem]);

  // Filter and sort model layers
  const filteredModelLayers = useMemo(() => {
    let layers = modelLayers;
    if (layerSearchQuery.trim()) {
      const q = layerSearchQuery.toLowerCase();
      layers = layers.filter(
        (layer) =>
          layer.threeJsName.toLowerCase().includes(q) ||
          layer.displayName?.toLowerCase().includes(q) ||
          layer.originalName?.toLowerCase().includes(q),
      );
    }
    if (layerSortKey) {
      const dir = layerSortAsc ? 1 : -1;
      layers = [...layers].sort((a, b) => {
        switch (layerSortKey) {
          case 'name':
            return dir * (a.displayName || a.threeJsName).localeCompare(b.displayName || b.threeJsName);
          case 'type':
            return dir * a.layerType.localeCompare(b.layerType);
          case 'chl':
            return dir * (Number(b.canHoldLuminaire) - Number(a.canHoldLuminaire));
          case 'lmn':
            return dir * (Number(b.isIlluminated) - Number(a.isIlluminated));
          default:
            return 0;
        }
      });
    }
    return layers;
  }, [modelLayers, layerSearchQuery, layerSortKey, layerSortAsc]);

  // Get layers that can hold luminaires
  const layersThatCanHoldLuminaires = useMemo(() => {
    return modelLayers.filter((layer) => layer.canHoldLuminaire);
  }, [modelLayers]);

  // Get illuminated layers (LMN)
  const illuminatedLayers = useMemo(() => {
    return modelLayers.filter((layer) => layer.isIlluminated);
  }, [modelLayers]);

  // Sort lamps alphabetically by name
  const sortedLamps = useMemo(() => {
    return [...lamps].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [lamps]);

  // Compute optimization state
  const optimizationState = useMemo(() => {
    const hasCHL = layersThatCanHoldLuminaires.length > 0;
    const hasLMN = illuminatedLayers.length > 0;
    const allLMNHaveNorm = illuminatedLayers.every(
      (layer) => layer.selectedNorm && layer.selectedNorm !== '' && layer.selectedNorm !== LAYER_NORM_NONE,
    );
    const hasLamps = lamps.length > 0;

    if (hasLamps && hasLMN && allLMNHaveNorm) {
      return 'ready_for_optimization'; // 2.3 - Luminaires added, ready for optimization
    } else if (hasCHL && hasLMN && allLMNHaveNorm) {
      return 'ready_for_autoposition'; // 2.2 - Layers set, ready for autopositioning
    } else {
      return 'not_ready'; // 2.1 - Missing CHL or LMN or Norm
    }
  }, [layersThatCanHoldLuminaires.length, illuminatedLayers, lamps.length]);

  // Helper function to update a single layer
  const updateLayer = (uuid: string, updates: Partial<ModelLayer>) => {
    const updatedLayers = modelLayers.map((layer) => (layer.uuid === uuid ? { ...layer, ...updates } : layer));
    setModelLayers(updatedLayers);
  };

  // Clean up highlight toggle timeout on unmount
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current !== null) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if ((!norms || norms.length === 0) && !normsLoadedRef.current) {
      dispatch(userReducer.actions.loadNorms());
      normsLoadedRef.current = true;
    }
  }, [dispatch, norms, checkedItems, selectedMeasurements]);

  // Check if autopositioning is in progress or calculation is in progress or render baking is in progress
  const isAutopositioningInProgress = checkIsAutopositioningInProgress(activeVersion);
  const isCalculationNotStarted = !isCalculationStarted(activeVersion);

  // Delete selected lamp: Delete or Backspace key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when user is typing in an input field
      const activeElement = document.activeElement;
      const isTyping = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA');

      if ((e.key === 'Delete' || e.key === 'Backspace') && isCalculationNotStarted && !isAutopositioningInProgress) {
        if (isTyping) return;

        const selectedLamp = lamps.find((lamp) => lamp.isSelected);
        if (selectedLamp) {
          e.preventDefault();
          removeLamp(selectedLamp.id);
        }
      }

      // Ctrl/Cmd+Shift+1 → Toggle CHL on selected layer
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'Digit1') {
        if (isTyping || !selectedLayer || !isCalculationNotStarted) return;
        e.preventDefault();
        const layer = modelLayers.find((l) => l.threeJsName === selectedLayer);
        if (layer) toggleLayerLuminaireHolder(layer.threeJsName);
      }

      // Ctrl/Cmd+Shift+2 → Toggle LMN on selected layer
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'Digit2') {
        if (isTyping || !selectedLayer || !isCalculationNotStarted) return;
        e.preventDefault();
        const layer = modelLayers.find((l) => l.threeJsName === selectedLayer);
        if (layer) {
          toggleLayerIllumination(layer.threeJsName);
          if (layer.isIlluminated) {
            updateLayerNorm(layer.threeJsName, LAYER_NORM_NONE);
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    lamps,
    isCalculationNotStarted,
    isAutopositioningInProgress,
    removeLamp,
    selectedLayer,
    modelLayers,
    toggleLayerLuminaireHolder,
    toggleLayerIllumination,
    updateLayerNorm,
  ]);

  // Check if calculation is in progress or render baking is in progress
  const isCalculationInProgress = isCalculationInProgressHelper(activeVersion);
  const isCalculationCompleted = isCalculationCompletedHelper(activeVersion);
  const totalLumenOutput = useMemo(() => {
    if (!isCalculationCompleted) return null;
    const outputs = lamps
      .map((lamp) => lamp.lumenOutput)
      .filter((output): output is number => typeof output === 'number' && Number.isFinite(output));
    if (outputs.length === 0) return null;
    return outputs.reduce((sum, output) => sum + output, 0);
  }, [lamps, isCalculationCompleted]);
  const isExportEnabled = canExportPdf(activeVersion, { frontendPdfPlotsEnabled });
  const exportTooltipKey = isExportEnabled
    ? 'workspace.export.pdf'
    : fullyAssigned && frontendPdfPlotsEnabled
      ? 'workspace.export.notAvailable'
      : fullyAssigned
        ? 'workspace.export.notAvailableMarketMatched'
        : 'workspace.export.notAvailable';

  // Per-version default expansion: when a version has no results yet, surface
  // the Layers + Luminaires sections (the user is in setup mode); once
  // results exist *and* the Results section can actually render, surface it
  // and tuck the setup sections away. Fires once per version id so manual
  // toggles after that stick.
  useEffect(() => {
    const vId = activeVersion?.id ?? null;
    if (vId === null) return;
    if (sectionsInitForVersionRef.current === vId) return;
    const hasRenderableResults =
      isCalculationCompleted && !!activeVersion?.measurementData && activeVersion.measurementData.length > 0;
    // Don't lock in the init until we've seen enough state to make the right
    // call (e.g. measurementData hasn't streamed in yet on a completed run).
    if (isCalculationCompleted && !hasRenderableResults) return;
    sectionsInitForVersionRef.current = vId;
    if (hasRenderableResults) {
      dispatch(setExpandedSections({ results: true, layers: false, lights: false }));
    } else {
      dispatch(setExpandedSections({ results: false, layers: true, lights: true }));
    }
  }, [activeVersion?.id, isCalculationCompleted, activeVersion?.measurementData, dispatch]);
  const isCalculationFailed = isJobFailed(activeVersion?.calculationJob);
  const isRenderBakingInProgress = isRenderBakingInProgressHelper(activeVersion);
  const isBakingJobCompleted = isJobCompleted(activeVersion?.renderBakingJob);
  const isBakingJobFailed = isJobFailed(activeVersion?.renderBakingJob);

  // Check if any lamps are not above model (N/A height)
  const hasLampsNotAboveModel = lamps.some((lamp) => lamp.distanceToGround === DISTANCE_TO_GROUND_NA);

  // Lamp similarity job status
  const isLampSimilarityInProgress = isJobInProgress(activeVersion?.lampSimilarityJob);
  const isLampSimilarityFailed = isJobFailed(activeVersion?.lampSimilarityJob);
  const lampSimilarityCandidates = activeVersion?.lampSimilarityResult?.candidates || [];

  const getSidebarContent = () => {
    if (isCalculationInProgress) {
      return {
        title: t('workspace.optimizationInProgress'),
        description: t('workspace.processingCalculations'),
        buttonText: t('workspace.optimizing'),
      };
    }

    if (isRenderBakingInProgress) {
      return {
        title: t('workspace.renderBakingInProgress'),
        description: t('workspace.processingCalculations'),
        buttonText: t('workspace.renderBaking'),
      };
    }

    if (isCalculationFailed) {
      return {
        title: t('workspace.optimizationFailed'),
        description: t('workspace.optimizationFailed'),
        buttonText: t('workspace.failed'),
      };
    }

    if (hasLampsNotAboveModel) {
      return {
        title: t('workspace.optimizationNotAvailable', { mode: t('workspace.optimization') }),
        description: t('workspace.lampsNotAboveModelWarning'),
        buttonText: t('workspace.optimization'),
      };
    }

    if (isMaxLampsReached) {
      return {
        title: t('workspace.optimizationNotAvailable', { mode: t('workspace.optimization') }),
        description: t('workspace.addLuminaireManuallyDisabled'),
        buttonText: t('workspace.optimization'),
      };
    }

    if (isOptimizationStarting) {
      return {
        title: t('workspace.allSetOptimization'),
        description: t('workspace.processingCalculations'),
        buttonText: t('workspace.preparing'),
      };
    }

    return {
      title: t('workspace.allSetOptimization'),
      description: t('workspace.luminairesAdded', { count: lamps.length }),
      buttonText: t('workspace.optimization'),
    };
  };

  const { title: sidebarTitle, description: sidebarDescription, buttonText: sidebarButtonText } = getSidebarContent();

  // Measurement results are available when:
  //   • ALL lamps are catalogue-assigned (frontend engine covers the whole scene), OR
  //   • Optimization completed (ML engine — covers optimized + future hybrid).
  const isMeasurementDataAvailable =
    (isCalculationCompleted && !!activeVersion?.measurementFileId) ||
    fullyAssigned ||
    (!!appliedLampSimilaritySelection && !!measurementData?.length);

  // Check if baked model is available
  const isBakedModelAvailable = isBakingJobCompleted && !!activeVersion?.bakedModelUrl;
  const renderTooltipKey = isBakedModelAvailable
    ? 'workspace.viewMode.render'
    : fullyAssigned
      ? 'workspace.viewMode.renderNotAvailableMarketMatched'
      : 'workspace.viewMode.renderNotAvailable';

  // View mode toggle handler
  type ViewMode = 'object' | 'measurement' | 'render' | 'realTimeRender';
  const { transitionToMode } = useViewModeTransition();
  const toggleViewMode = (mode: ViewMode) => {
    transitionToMode(mode);
  };

  // WebGPU probe state for the real-time render mode (hidden if unsupported)
  const [webGpuSupported, setWebGpuSupported] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    void probeWebGpuSupport().then((r) => {
      if (!cancelled) setWebGpuSupported(r.supported);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // Real-time render follows the same rule as measurement.
  const isRealTimeRenderAvailable = uploadedModels.length > 0 && (isCalculationCompleted || fullyAssigned);

  const handleLayerNameDoubleClick = (layer: ModelLayer, e: React.MouseEvent) => {
    e.stopPropagation();

    // Only allow editing if calculations haven't started
    if (!isCalculationNotStarted) {
      return;
    }

    setEditingLayerId(layer.uuid);
    setEditingDisplayName(layer.displayName || layer.threeJsName);
  };

  const handleSaveDisplayName = (layerUuid: string) => {
    if (editingDisplayName.trim()) {
      updateLayerDisplayName(layerUuid, editingDisplayName.trim());
    }
    setEditingLayerId(null);
    setEditingDisplayName('');
  };

  const handleCancelEdit = () => {
    setEditingLayerId(null);
    setEditingDisplayName('');
  };

  // Handle type button click - opens type dropdown
  const handleTypeClick = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (!isCalculationNotStarted) return;
    // Close norm dropdown if open
    setExpandedNormLayer(null);
    // Toggle the type list expansion
    if (expandedTypeLayer === layer.threeJsName) {
      setExpandedTypeLayer(null);
    } else {
      // Get button position for dropdown placement
      const button = e.currentTarget as HTMLButtonElement;
      const rect = button.getBoundingClientRect();
      setTypeDropdownPosition({ top: rect.bottom + 4, left: rect.left });
      setExpandedTypeLayer(layer.threeJsName);
    }
  };

  // Handle type selection from dropdown
  const handleTypeSelect = (layerName: string, type: keyof typeof LAYER_TYPES) => {
    updateLayerType(layerName, type);
    setExpandedTypeLayer(null);
  };

  // Handle norm button click - opens norm selection panel
  const handleNormClick = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (!isCalculationNotStarted) return;
    // Only allow norm selection when illuminated is checked
    if (!layer.isIlluminated) return;
    // Close type dropdown if open
    setExpandedTypeLayer(null);
    // Toggle the norm list expansion
    if (expandedNormLayer === layer.threeJsName) {
      setExpandedNormLayer(null);
    } else {
      setExpandedNormLayer(layer.threeJsName);
    }
  };

  // Handle norm selection - close the norm list after selecting
  const handleNormSelect = (layerName: string, norm: string) => {
    updateLayerNorm(layerName, norm);
    setExpandedNormLayer(null);
  };

  // Handle CHL (Can Hold Light) toggle
  const handleCHLToggle = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (!isCalculationNotStarted) return;
    toggleLayerLuminaireHolder(layer.threeJsName);
  };

  // Handle Lmn (Illuminated) toggle
  const handleLmnToggle = (e: React.MouseEvent, layer: ModelLayer) => {
    e.stopPropagation();
    if (!isCalculationNotStarted) return;
    toggleLayerIllumination(layer.threeJsName);
    // If turning off illumination, also clear the norm
    if (layer.isIlluminated) {
      updateLayerNorm(layer.threeJsName, LAYER_NORM_NONE);
    }
  };

  // Handle layer luminaire height change
  const handleLayerHeightChange = (uuid: string, height: number) => {
    const heightInMeters = fromDisplayLength(height, unitSystem);
    if (isNaN(heightInMeters)) return;
    const clamped = Math.min(Math.max(heightInMeters, MIN_LUMINAIRE_HEIGHT_METERS), MAX_LUMINAIRE_HEIGHT_METERS);
    updateLayer(uuid, { desiredLuminaireHeight: clamped });
  };

  // Handle start autopositioning
  const handleStartAutoPositioning = () => {
    // Check if at least one layer has canHoldLuminaire set to true
    const hasCanHoldLuminaire = modelLayers.some((layer) => layer.canHoldLuminaire);

    if (!hasCanHoldLuminaire) {
      dispatch(
        toastReducer.actions.showToast({
          message: 'workspace.toast.atLeastOneLayerLuminaireEnabled',
          type: 'warning',
        }),
      );
      return;
    }

    const height = parseFloat(luminaireHeightInput);
    const heightInMeters = fromDisplayLength(height, unitSystem);

    if (!isNaN(heightInMeters) && heightInMeters < MIN_LUMINAIRE_HEIGHT_METERS) {
      setLuminaireHeightInput(heightInputMin.toFixed(2));
      setDesiredLuminaireHeight(MIN_LUMINAIRE_HEIGHT_METERS);
    } else if (!isNaN(heightInMeters) && heightInMeters > MAX_LUMINAIRE_HEIGHT_METERS) {
      setLuminaireHeightInput(heightInputMax.toFixed(2));
      setDesiredLuminaireHeight(MAX_LUMINAIRE_HEIGHT_METERS);
    } else if (!isNaN(heightInMeters)) {
      setDesiredLuminaireHeight(heightInMeters);
    }

    autoPositionLights();
  };

  const handleLampClick = (lampId: string) => {
    // Toggle lamp selection - if the lamp is already selected, deselect it
    if (lamps.find((lamp) => lamp.id === lampId)?.isSelected) {
      selectLamp('');
    } else {
      selectLamp(lampId);
      setSelectedLayer(null);
    }
  };

  const handleOpen3dPreview = async (iesFileId: number, lampName: string) => {
    const requestId = ++latestIesPreviewRequestIdRef.current;
    setIes3dPreview({ isOpen: true, lampName, iesUrl: undefined });
    try {
      const { data } = await httpGET(`/files/${iesFileId}/presigned-url/download`);
      if (requestId !== latestIesPreviewRequestIdRef.current) return;
      setIes3dPreview((prev) => ({ ...prev, iesUrl: data.url }));
    } catch {
      if (requestId !== latestIesPreviewRequestIdRef.current) return;
      // Modal will show error state; leave iesUrl undefined
    }
  };

  // Auto-scroll sidebar when selection changes (e.g. from the 3D scene)
  const selectedLampId = useMemo(() => lamps.find((l) => l.isSelected)?.id ?? null, [lamps]);

  useEffect(() => {
    if (selectedLayer && selectedLayer !== prevSelectedLayerRef.current) {
      // Don't auto-scroll to layers section when results section is expanded
      // (user is likely clicking a result card, not selecting from the 3D scene)
      if (!expandedSections.results) {
        openSection('layers');
        requestAnimationFrame(() => {
          const el = layerListRef.current?.querySelector(`[data-layer-id="${CSS.escape(selectedLayer)}"]`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      }
    }
    prevSelectedLayerRef.current = selectedLayer;
  }, [selectedLayer, openSection, expandedSections.results]);

  useEffect(() => {
    if (selectedLampId && selectedLampId !== prevSelectedLampIdRef.current) {
      // Open "lights" section and scroll to the selected lamp
      openSection('lights');
      requestAnimationFrame(() => {
        const el = lampListRef.current?.querySelector(`[data-lamp-id="${CSS.escape(selectedLampId)}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
    prevSelectedLampIdRef.current = selectedLampId;
  }, [selectedLampId, openSection]);

  // Resolve "Product · Variant" labels for all catalogue-assigned lamps.
  useEffect(() => {
    const catalogueLamps = lamps.filter((l) => l.productId != null && l.variantId != null);
    if (catalogueLamps.length === 0) return;
    let cancelled = false;
    catalogueLamps.forEach((lamp) => {
      const key = `${lamp.productId}-${lamp.variantId}`;
      if (catalogueLabels[key]) return; // already resolved
      void fetchCatalogueProduct(lamp.productId!).then((info) => {
        if (cancelled) return;
        const variantName = info?.variants?.find((v) => Number(v.id) === lamp.variantId)?.name;
        const label = variantName ?? info?.name ?? `Product #${lamp.productId}`;
        setCatalogueLabels((prev) => ({ ...prev, [key]: label }));
      });
    });
    return () => {
      cancelled = true;
    };
  }, [lamps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-reload similarity details when the Market Matches section is already expanded but
  // details were cleared (e.g. after a version switch). Without this, the Apply buttons
  // remain hidden until the user manually closes and reopens the section.
  useEffect(() => {
    if (
      expandedSections.marketMatches &&
      lampSimilarityCandidates.length > 0 &&
      lampSimilarityDetails.length === 0 &&
      !isLoadingLampSimilarityDetails
    ) {
      dispatch(loadLampSimilarityDetails());
    }
  }, [
    expandedSections.marketMatches,
    lampSimilarityCandidates.length,
    lampSimilarityDetails.length,
    isLoadingLampSimilarityDetails,
    dispatch,
  ]);

  const handleTamanOptimisation = () => {
    if (lamps.length === 0) {
      dispatch(
        toastReducer.actions.showToast({
          message: 'workspace.toast.atLeastOneLampRequired',
          type: 'warning',
        }),
      );
      return;
    }

    // Check if any lamps have distanceToGround = -1
    const lampsNotAboveModel = lamps.filter((lamp) => lamp.distanceToGround === DISTANCE_TO_GROUND_NA);
    if (lampsNotAboveModel.length > 0) {
      const lampNames = lampsNotAboveModel.map((lamp) => lamp.name).join(', ');
      dispatch(
        toastReducer.actions.showToast({
          message: i18n.t('workspace.toast.lampsNotAboveModel', { lampNames }),
          type: 'warning',
        }),
      );
      return;
    }

    // Optimization is always the backend/ml engine (the frontend never optimizes —
    // it only re-computes measurement points from an existing IES, triggered via the
    // measurement source toggle / grid-spacing control).
    startTamanOptimization();
    // Open the results section
    if (!expandedSections.results) {
      toggleSection('results');
    }
  };

  // Handler to instantly add a lamp at (0, 0, 0) and move camera to view it
  const handleInstantAddLamp = () => {
    // Clear any active measurement tools
    if (activeToolbarTool === 'measurement') {
      clearMeasurementPoints();
      setActiveToolbarTool('selection');
    }
    if (activeToolbarTool === 'lampDistance') {
      clearLampDistanceSelection();
      setActiveToolbarTool('selection');
    }

    // Immediately add a lamp at (0, 0, 0) without showing the modal
    const newLamp = {
      name: `Luminaire ${lamps.length + 1}`,
      position: [0, 0, 0] as [number, number, number],
      distanceToGround: desiredLuminaireHeight,
    };
    addLamp(newLamp);

    // Move camera to (0, 0, 20) looking at the new lamp
    setTargetCameraPosition({
      position: [0, 0, 20],
      lookAt: [0, 0, 0],
    });

    // Close autoposition sidebar if open
    setShowAutoPositionSidebar(false);
  };

  const renderMeasurementExportOptions = () => {
    // Get dynamic measurement options from activeVersion.meshes where normId is not null (illuminated layers)
    if (!activeVersion) return null;

    const illuminatedLayers = getIlluminatedLayers(activeVersion);

    // Filter layers based on search query
    const filteredLayers = illuminatedLayers.filter((mesh) => {
      if (!measurementSearchQuery) return true;
      const layerTitle = createLayerTitle(
        mesh.displayName,
        mesh.normId,
        norms,
        mesh.targets?.length ? 'custom' : undefined,
      );
      return layerTitle.toLowerCase().includes(measurementSearchQuery.toLowerCase());
    });

    if (filteredLayers.length === 0) {
      return (
        <div className="py-4 text-center text-gray-300 text-sm">
          {measurementSearchQuery ? t('workspace.noMeasurementsMatch') : t('workspace.noMeasurementsAvailable')}
        </div>
      );
    }

    return (
      <div>
        {measurementSearchQuery && (
          <div className="text-xs text-lime-400 mb-2">
            {t('workspace.showingMeasurements', {
              filtered: filteredLayers.length,
              total: illuminatedLayers.length,
            })}
          </div>
        )}
        {filteredLayers.map((mesh, index) => {
          const layerTitle = createLayerTitle(
            mesh.displayName,
            mesh.normId,
            norms,
            mesh.targets?.length ? 'custom' : undefined,
          );
          return (
            <div key={mesh.uuid} className={index < filteredLayers.length - 1 ? 'border-b border-black' : ''}>
              <div
                className={`-mx-2 flex items-center gap-3 rounded-md px-2 py-3 cursor-pointer hover:bg-lime-600/30 transition-colors`}
                onClick={() => handleMeasurementCheck(layerTitle)}
              >
                {/* Solid black square checkbox */}
                <div
                  className={`h-5 w-5 rounded-sm flex items-center justify-center transition-colors duration-200 ${
                    selectedMeasurements[layerTitle] ? 'bg-lime-400' : 'bg-black border border-black'
                  }`}
                >
                  {selectedMeasurements[layerTitle] && <Check className="h-3 w-3 text-black" />}
                </div>
                <span className={`text-[${greyP2}] text-sm`}>{layerTitle}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const exportItems = [
    { id: 'cover-page', label: t('workspace.export.items.coverPage'), submenu: false },
    { id: 'table-of-content', label: t('workspace.export.items.tableOfContents'), submenu: false },
    { id: 'lamp-info', label: t('workspace.export.items.luminaireInfo'), submenu: false },
    { id: 'layers-summary', label: t('workspace.export.items.layersSummary'), submenu: false },
    { id: 'graphs', label: t('workspace.export.items.layerPlots'), submenu: true },
    { id: 'global-plots', label: t('workspace.export.items.globalPlots'), submenu: false },
  ];

  // Which photometries (plots and table rows) go into the PDF; the last selected one can't be removed.
  const selectedPhotometryCount = PDF_PHOTOMETRY_ENTRIES.filter((entry) =>
    isPhotometryIncluded(selectedPhotometries, entry.photometry),
  ).length;
  const renderPhotometryExportOptions = () => (
    <div>
      <p className="pb-2 text-xs text-gray-400">{t('workspace.export.photometriesHint')}</p>
      {PDF_PHOTOMETRY_ENTRIES.map((entry, index) => {
        const checked = isPhotometryIncluded(selectedPhotometries, entry.photometry);
        const locked = checked && selectedPhotometryCount <= 1;
        return (
          <div
            key={entry.photometry}
            className={index < PDF_PHOTOMETRY_ENTRIES.length - 1 ? 'border-b border-black' : ''}
          >
            <div
              className={`-mx-2 flex items-center gap-3 rounded-md px-2 py-3 transition-colors ${
                locked ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-lime-600/30'
              }`}
              onClick={() => handlePhotometryCheck(entry.photometry)}
              title={locked ? t('workspace.export.photometriesKeepOne') : undefined}
            >
              <div
                className={`h-5 w-5 rounded-sm flex items-center justify-center transition-colors duration-200 ${
                  checked ? 'bg-lime-400' : 'bg-black border border-black'
                }`}
              >
                {checked && <Check className="h-3 w-3 text-black" />}
              </div>
              <span className={`text-[${greyP2}] text-sm`}>
                {MEASUREMENT_VISUALIZATION_LABELS[entry.visualization]}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderExportOptions = () => (
    <div>
      {exportItems.map((item, index) => (
        <div key={item.id} className={index < exportItems.length - 1 ? 'border-b border-black' : ''}>
          <div
            className={`-mx-2 flex items-center gap-3 rounded-md px-2 py-3 cursor-pointer hover:bg-lime-600/30 transition-colors`}
            onClick={() => handleCheck(item.id)}
          >
            {/* Solid black square checkbox */}
            <div
              className={`h-5 w-5 rounded-sm flex items-center justify-center transition-colors duration-200 ${
                checkedItems[item.id] ? 'bg-lime-400' : 'bg-black border border-black'
              }`}
            >
              {checkedItems[item.id] && <Check className="h-3 w-3 text-black" />}
            </div>
            <span className={`text-[${greyP2}] text-sm`}>{item.label}</span>
          </div>
        </div>
      ))}
    </div>
  );

  // Get display names for measurement layers
  // Map layer name → original name. Memoized so it doesn't return a new object on every
  // render (which made react-redux re-run consumers and warn about unmemoized selectors).
  const layerDisplayNames = useMemo(() => {
    const displayNames: Record<string, string> = {};
    if (!measurementData) return displayNames;
    measurementData.forEach((layer) => {
      const match = modelLayers.find((l) => l.threeJsName === layer.name);
      displayNames[layer.name] = match?.originalName || layer.name;
    });
    return displayNames;
  }, [modelLayers, measurementData]);

  // Calculate satisfaction percentage for a layer
  const calculateLayerSatisfaction = (
    layer: MeasurementData,
  ): { satisfied: number; total: number; percentage: number } => {
    const { expectedValues, actualValues } = layer;
    if (!actualValues || expectedValues.length === 0) return { satisfied: 0, total: 0, percentage: 0 };

    let satisfiedCount = 0;
    let totalCount = 0;

    for (const expected of expectedValues) {
      const actual = actualValues.find((a) => a.function === expected.function && a.type === expected.type);
      if (actual) {
        totalCount++;
        const pair: ExpectedActualPair = {
          expected: expected.value,
          actual: actual.value,
          function: expected.function,
          type: expected.type,
        };
        if (isTargetSatisfied(pair)) {
          satisfiedCount++;
        }
      }
    }

    return {
      satisfied: satisfiedCount,
      total: totalCount,
      percentage: totalCount > 0 ? Math.round((satisfiedCount / totalCount) * 100) : 0,
    };
  };

  // Render Results content
  const renderResultsContent = () => {
    if (!measurementData || measurementData.length === 0) {
      return <div className="px-8 py-3 text-sm text-gray-300">{t('workspace.measurementNotAvailable')}</div>;
    }

    return (
      <div className="space-y-1.5 p-2">
        {measurementData.map((layer) => {
          const { expectedValues, actualValues } = layer;
          const displayName = layerDisplayNames[layer.name] || layer.name;

          if (!actualValues || expectedValues.length === 0) return null;

          const pairs: ExpectedActualPair[] = [];
          for (const expected of expectedValues) {
            const actual = actualValues.find((a) => a.function === expected.function && a.type === expected.type);
            if (actual) {
              pairs.push({
                expected: expected.value,
                actual: actual.value,
                function: expected.function,
                type: expected.type,
              });
            }
          }

          const { satisfied, total } = calculateLayerSatisfaction(layer);

          const matchedLayer = modelLayers.find(
            (item) => item.threeJsName === layer.name || item.originalName === layer.name,
          );
          const normName = layer.normId
            ? getNormName(layer.normId, norms)
            : matchedLayer?.selectedNorm === LAYER_NORM_CUSTOM
              ? t('workspace.customTargets.chip')
              : '';

          return (
            <div
              key={layer.uuid}
              className={`rounded-lg font-inter overflow-hidden border cursor-pointer transition-all duration-200 ${
                selectedLayer === layer.name
                  ? 'border-lime-400 ring-2 ring-lime-400/80'
                  : hoveredLayer === layer.name
                    ? 'border-lime-400/50'
                    : 'border-neutral-700'
              }`}
              style={{
                background: 'linear-gradient(170deg, rgba(79, 209, 46, 0.15) 0%, rgba(35, 35, 35, 0.95) 100%)',
              }}
              onClick={() => {
                setSelectedLayer(layer.name === selectedLayer ? null : layer.name);
              }}
              onMouseEnter={() => setHoveredLayer(layer.name)}
              onMouseLeave={() => setHoveredLayer(null)}
            >
              {/* Title row — single line */}
              <div className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] min-w-0">
                <span className="text-[13px] font-medium text-white truncate min-w-0" title={displayName}>
                  {displayName}
                </span>
                <div className="flex items-center gap-1 ml-auto flex-shrink-0">
                  {normName && (
                    <>
                      <span className="text-gray-300">Norm:</span>
                      <span className="text-gray-200 truncate max-w-[80px]" title={normName}>
                        {normName}
                      </span>
                      <span className="text-gray-500">,</span>
                    </>
                  )}
                  <span className="text-gray-300">Satisfied:</span>
                  <span className="whitespace-nowrap">
                    <span className="text-lime-400 font-semibold">{satisfied}</span>
                    <span className="text-gray-300">/{total}</span>
                  </span>
                </div>
              </div>

              {/* Metrics table */}
              <div className="px-2.5 pb-2">
                {/* Table header */}
                <div className="grid grid-cols-12 items-center text-[9px] text-gray-300 uppercase tracking-wide mb-0.5">
                  <div className="col-span-1" />
                  <div className="col-span-3" />
                  <div className="col-span-4">{t('workspace.value', 'Value')}</div>
                  <div className="col-span-4">{t('workspace.targetValue', 'Required')}</div>
                </div>

                {pairs.map((pair) => {
                  const uom = getPhotometricUnit(pair.function);
                  const isSatisfied = isTargetSatisfied(pair);
                  const operator = getComparisonOperator(pair.type);

                  return (
                    <div
                      key={`${pair.function}-${pair.type}`}
                      className="grid grid-cols-12 items-center text-[11px] h-[20px] border-b border-white/5"
                    >
                      {/* Status dot */}
                      <div className="col-span-1 flex justify-center">
                        <div
                          className={`w-2 h-2 rounded-full ${isSatisfied ? 'bg-lime-400' : 'bg-red-400'}`}
                          title={isSatisfied ? t('workspace.targetMet') : t('workspace.targetNotMet')}
                        />
                      </div>

                      {/* Metric symbol */}
                      <div className="col-span-3 text-gray-300">{getPhotometricSymbol(pair.function, pair.type)}</div>

                      {/* Actual value */}
                      <div className={`col-span-4 ${getActualValueColor(pair)} font-medium`}>
                        {pair.actual?.toFixed(2)}
                        {uom ? ` ${uom}` : ''}
                      </div>

                      {/* Target value with comparison operator */}
                      <div className="col-span-4 text-gray-300">
                        {operator} {pair.expected?.toFixed(2)}
                        {uom ? ` ${uom}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderMarketMatchesContent = () => {
    if (isLampSimilarityInProgress) {
      return (
        <div className="relative flex items-start justify-between px-3 py-6 h-[225px] overflow-hidden bg-black">
          <div className="z-10">
            <h3 className="text-white font-semibold text-sm">{t('workspace.marketMatchingHeading')}</h3>
          </div>

          <div
            className="absolute"
            style={{
              width: 369,
              height: 372,
              right: -369 * 0.25,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <CircularProgress size={369} stroke={34} spinDuration={4800} />
          </div>
        </div>
      );
    }

    if (lampSimilarityCandidates.length === 0) {
      return (
        <MarketMatchPrefiltersPanel
          onStart={(filters) => {
            if (project && activeVersion) {
              dispatch(startLampSimilarity({ projectId: project.id, versionId: activeVersion.id, filters }));
            }
          }}
        />
      );
    }

    // Show spinner while loading details
    if (isLoadingLampSimilarityDetails) {
      return (
        <div className="space-y-4 px-3 py-4 bg-black">
          <div className="flex items-center gap-2 text-stone-400 text-sm">
            <Loader2 size={16} className="animate-spin" />
            <span>{t('workspace.loadingLampDetails')}</span>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="relative flex items-start justify-between px-3 py-6 h-[150px] overflow-hidden bg-black">
          <div className="z-10 space-y-1">
            <h3 className="text-white font-semibold text-sm">{t('workspace.matchingCompleted')}</h3>
            <p className="text-stone-400 text-xs">
              {t('workspace.statusLamps', { count: lampSimilarityCandidates.length })}
            </p>
          </div>

          <div
            className="absolute"
            style={{
              width: 369,
              height: 372,
              right: -369 * 0.25,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <CircularProgress size={369} stroke={34} completed />
          </div>
        </div>

        <div className="space-y-4 px-3 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 400px)' }}>
          <div className="space-y-2">
            {lampSimilarityCandidates.map((candidate) => {
              const candidateMeasurementFileId = candidate.measurementFile?.id;
              const detail = lampSimilarityDetails.find(
                (d) => d.lampId === candidate.lampId && d.measurementFileId === candidateMeasurementFileId,
              );
              const previewMode = getMarketMatchPreviewMode(userFeatureFlags, detail, activeVersion);
              const canApply = canApplyMarketMatchCandidate(userFeatureFlags, detail, activeVersion);
              const isApplied =
                appliedLampSimilaritySelection?.lampId === candidate.lampId &&
                appliedLampSimilaritySelection?.measurementFileId === candidateMeasurementFileId;
              const displayAggregateResults =
                isApplied && measurementData?.length
                  ? measurementDataToAggregateResults(measurementData)
                  : candidate.aggregateResults;
              const targetsCount = displayAggregateResults?.length
                ? getTargetsSatisfiedCountFromAggregateResults(displayAggregateResults)
                : getTargetsSatisfiedCount(candidate);
              const allSatisfied =
                targetsCount.total > 0 ? targetsCount.satisfied === targetsCount.total : candidate.satisfiesNorms;
              return (
                <div
                  key={`${candidate.lampId}-${candidateMeasurementFileId ?? 'no-file'}`}
                  className="group flex gap-3 p-2 rounded-lg transition-all duration-300 hover:bg-stone-800 border border-transparent hover:border-stone-700 shadow-sm"
                >
                  {/* Image */}
                  {detail?.imageUrl && (
                    <div className="relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-stone-900 border border-stone-700 group-hover:border-lime-500/50 transition-colors flex items-center justify-center p-1">
                      <img
                        src={detail.imageUrl}
                        alt={detail.name || `Lamp ${candidate.lampId}`}
                        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                    <div className="min-w-0">
                      {detail?.manufacturerName && (
                        <p className="text-stone-400 text-xs truncate">{detail.manufacturerName}</p>
                      )}
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="text-white text-sm font-bold truncate group-hover:text-lime-400 transition-colors">
                          {detail?.name || t('workspace.lampId', { id: candidate.lampId })}
                        </h3>
                        <span className="text-lime-400 font-bold text-sm whitespace-nowrap">
                          {candidate.lumenOutput > 0 ? Math.round(candidate.lumenOutput).toLocaleString() : 'N/A'}{' '}
                          <span className="text-xs font-normal opacity-70">lm</span>
                        </span>
                      </div>
                      <a
                        href={buildCatalogueUrl({
                          productId: candidate.lampId,
                          seriesId: detail?.seriesId,
                          manufacturerId: detail?.manufacturerId,
                        })}
                        className="text-lime-400 text-xs hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('workspace.viewInCatalogue')}
                      </a>
                    </div>

                    <div className="flex items-end justify-between mt-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!displayAggregateResults || displayAggregateResults.length === 0) return;
                          setMarketMatchResultsPreview({
                            lampName: detail?.name || t('workspace.lampId', { id: candidate.lampId }),
                            aggregateResults: displayAggregateResults,
                          });
                        }}
                        className={`text-xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 transition-colors ${
                          allSatisfied
                            ? 'text-lime-500/90 bg-lime-500/10 hover:bg-lime-500/20'
                            : 'text-red-500/90 bg-red-500/10 hover:bg-red-500/20'
                        } ${displayAggregateResults?.length ? 'cursor-pointer' : 'cursor-default'}`}
                        disabled={!displayAggregateResults?.length}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${allSatisfied ? 'bg-lime-500' : 'bg-red-500'}`} />
                        {t('workspace.targetsSatisfied', {
                          satisfied: targetsCount.satisfied,
                          total: targetsCount.total,
                        })}
                      </button>
                      <div className="flex items-center gap-1.5">
                        {canApply && previewMode && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(
                                workspaceReducer.actions.applyLampSimilarityMeasurement({
                                  lampId: candidate.lampId,
                                  measurementFileId: candidateMeasurementFileId,
                                  previewMode,
                                }),
                              );
                            }}
                            className={`text-xs font-semibold px-2 py-0.5 rounded transition-colors ${
                              isApplied
                                ? 'bg-lime-500 text-black cursor-default'
                                : 'bg-stone-700 text-white hover:bg-lime-500 hover:text-black'
                            }`}
                          >
                            {isApplied ? t('workspace.applied') : t('workspace.apply')}
                          </button>
                        )}
                        {marketMatchToVersionEnabled && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!project || !activeVersion || isCreatingVersion) return;
                              dispatch(
                                createVersionFromMarketMatch({
                                  projectId: project.id,
                                  sourceVersionId: activeVersion.id,
                                  lampId: candidate.lampId,
                                  measurementFileId: candidateMeasurementFileId,
                                }),
                              );
                            }}
                            disabled={isCreatingVersion}
                            className="text-xs font-semibold px-2 py-0.5 rounded transition-colors bg-stone-700 text-white hover:bg-lime-500 hover:text-black disabled:opacity-50 disabled:cursor-wait"
                          >
                            {t('workspace.toNewVersion')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  };

  const { pdfUrl, isExportLoading, isGeneratingPdf } = usePdfExport();

  if (!isOpen) return null;

  const renderWorkspaceLayerRow = (layer: ModelLayer) => (
    <div
      key={layer.threeJsName}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/layer-uuid', layer.uuid);
      }}
      data-layer-id={layer.threeJsName}
      className={`flex items-center justify-between py-2 px-2 cursor-pointer border-b border-gray-800 ${
        selectedLayer === layer.threeJsName
          ? 'bg-red-400/35 ring-1 ring-inset ring-red-400/50'
          : hoveredLayer === layer.threeJsName
            ? 'bg-orange-400/20'
            : 'hover:bg-orange-400/10'
      }`}
      onClick={() => handleLayerClick(layer.threeJsName, layer.originalName)}
      onMouseEnter={() => setHoveredLayer(layer.threeJsName)}
      onMouseLeave={() => setHoveredLayer(null)}
    >
      <div className="flex items-center gap-2 overflow-hidden flex-1 mr-2">
        <button
          onClick={(e) => handleTypeClick(e, layer)}
          className={`w-6 h-6 flex items-center justify-center border rounded hover:border-lime-500 transition shrink-0 ${
            layer.layerType === 'Unspecified' ? 'bg-gray-600 border-gray-500' : 'bg-transparent border-gray-600'
          } ${!isCalculationNotStarted ? 'opacity-60 cursor-not-allowed' : ''}`}
          title={t('workspace.layerTypeTitle', { type: layerTypeLabel(t, layer.layerType) })}
          disabled={!isCalculationNotStarted}
        >
          <LayerTypeIcon type={layer.layerType} style={{ width: 14, height: 14, color: 'white' }} />
        </button>
        {/* Color picker removed — color is now driven by CHL/LMN state */}
        {editingLayerId === layer.uuid ? (
          <input
            type="text"
            value={editingDisplayName}
            onChange={(e) => setEditingDisplayName(e.target.value)}
            onBlur={() => handleSaveDisplayName(layer.uuid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveDisplayName(layer.uuid);
              else if (e.key === 'Escape') handleCancelEdit();
            }}
            className="text-sm bg-black border border-lime-500 rounded px-1 py-0 text-white focus:outline-none flex-1 min-w-0"
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`text-sm truncate ${
              isCalculationNotStarted ? 'cursor-pointer hover:text-lime-400' : 'cursor-not-allowed opacity-60'
            } ${selectedLayer === layer.threeJsName ? 'text-red-300' : hoveredLayer === layer.threeJsName ? 'text-orange-300' : 'text-white'}`}
            onDoubleClick={isCalculationNotStarted ? (e) => handleLayerNameDoubleClick(layer, e) : undefined}
            title={isCalculationNotStarted ? t('workspace.layerNameDoubleClick') : t('workspace.cannotEditLayerName')}
          >
            {layer.displayName}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={(e) => handleCHLToggle(e, layer)}
          className={`relative w-9 h-5 rounded-full bg-black transition-colors duration-200 ease-in-out focus:outline-none ${
            !isCalculationNotStarted ? 'opacity-60 cursor-not-allowed' : ''
          } ${highlightToggles ? 'animate-highlight-toggle' : ''}`}
          style={{ backgroundColor: '#000' }}
          disabled={!isCalculationNotStarted}
        >
          <div
            className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
              layer.canHoldLuminaire ? 'bg-sky-500 translate-x-4' : 'bg-gray-400 translate-x-0'
            }`}
          />
        </button>
        <button
          onClick={(e) => handleLmnToggle(e, layer)}
          className={`relative w-9 h-5 rounded-full bg-black transition-colors duration-200 ease-in-out focus:outline-none ${
            !isCalculationNotStarted ? 'opacity-60 cursor-not-allowed' : ''
          } ${highlightToggles ? 'animate-highlight-toggle' : ''}`}
          style={{ backgroundColor: '#000' }}
          disabled={!isCalculationNotStarted}
        >
          <div
            className={`absolute top-0.5 bottom-0.5 left-0.5 w-4 h-4 rounded-full transition-transform duration-200 ease-in-out ${
              layer.isIlluminated ? 'bg-lime-400 translate-x-4' : 'bg-gray-400 translate-x-0'
            }`}
          />
        </button>
        <div
          onClick={(e) => handleNormClick(e, layer)}
          className={`min-w-12 px-1 h-6 flex items-center justify-center text-xs rounded ${
            layer.isIlluminated && isCalculationNotStarted
              ? 'cursor-pointer hover:bg-stone-700'
              : 'cursor-not-allowed opacity-50'
          }`}
        >
          {layer.selectedNorm && layer.selectedNorm !== LAYER_NORM_NONE ? (
            <span
              className="text-lime-400 font-medium truncate"
              title={
                layer.customTargets?.length
                  ? layer.customTargets.map((target) => `${target.function} ${target.type} ${target.value}`).join(', ')
                  : undefined
              }
            >
              {layer.selectedNorm === LAYER_NORM_CUSTOM ? t('workspace.customTargets.chip') : layer.selectedNorm}
            </span>
          ) : (
            <span className="text-gray-300">None</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div
        className="text-white flex flex-col border-r border-neutral-700 w-[455px] sidebar min-h-0 flex-1"
        style={{ backgroundColor: greyP4 }}
      >
        {/* The sidebar header (version dropdown + breadcrumb) used to live
            here. Version controls moved to the global navbar
            (WorkspaceVersionBar) and the exports view already has its own
            in-panel header, so this slot is intentionally empty. */}

        {/* View Mode Section - Hidden when exports panel is open */}
        {!showExportsPanel && (
          <div className="p-4 border-b border-black">
            {/* Toolbar Row: View Mode Selector, Texture Toggle, View Point Dropdown */}
            <div className="flex items-center gap-4">
              {/* 1. View Mode Selector (Left) - Segmented Control */}
              <div className="flex bg-greyP6 p-1 rounded-lg gap-1">
                <Tooltip title={t('workspace.viewMode.scene')}>
                  <button
                    className={`p-2 flex justify-center items-center cursor-pointer transition-all rounded ${
                      activeViewModes?.object ? 'bg-greyP5 text-lime-400 shadow-sm' : 'text-white hover:text-white'
                    }`}
                    onClick={() => toggleViewMode('object')}
                  >
                    <LightingRender />
                  </button>
                </Tooltip>
                <Tooltip
                  title={
                    !isMeasurementDataAvailable
                      ? t('workspace.viewMode.measurementNotAvailable')
                      : t('workspace.viewMode.measurement')
                  }
                >
                  <button
                    className={`p-2 flex justify-center items-center cursor-pointer transition-all rounded ${
                      activeViewModes?.measurement ? 'bg-greyP5 text-lime-400 shadow-sm' : 'text-white hover:text-white'
                    } ${!isMeasurementDataAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => toggleViewMode('measurement')}
                    disabled={!isMeasurementDataAvailable}
                  >
                    <RasterIcon size={20} />
                  </button>
                </Tooltip>
                <Tooltip title={t(renderTooltipKey)}>
                  <button
                    className={`p-2 flex justify-center items-center cursor-pointer transition-all rounded ${
                      activeViewModes?.render ? 'bg-greyP5 text-lime-400 shadow-sm' : 'text-white hover:text-white'
                    } ${!isBakedModelAvailable ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onClick={() => toggleViewMode('render')}
                    disabled={!isBakedModelAvailable}
                  >
                    <LightingCustom />
                  </button>
                </Tooltip>
                <Tooltip
                  title={
                    webGpuSupported === false
                      ? t('workspace.viewMode.realTimeRenderUnsupported')
                      : !isRealTimeRenderAvailable
                        ? t('workspace.viewMode.realTimeRenderNotAvailable')
                        : t('workspace.viewMode.realTimeRender')
                  }
                >
                  <span>
                    <button
                      className={`p-2 flex justify-center items-center cursor-pointer transition-all rounded ${
                        activeViewModes?.realTimeRender
                          ? 'bg-greyP5 text-lime-400 shadow-sm'
                          : 'text-white hover:text-white'
                      } ${!isRealTimeRenderAvailable || webGpuSupported === false ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => toggleViewMode('realTimeRender')}
                      disabled={!isRealTimeRenderAvailable || webGpuSupported === false}
                    >
                      <Zap size={20} />
                    </button>
                  </span>
                </Tooltip>
              </div>

              {/* 2b. Download Blender project (Admin only, render view) */}
              {activeViewModes?.render && user?.roles?.includes('ADMIN') && (
                <Tooltip title={t('workspace.downloadBlenderProject')}>
                  <button
                    type="button"
                    aria-label={t('workspace.downloadBlenderProject')}
                    onClick={() => {
                      if (project?.id != null && activeVersion?.id != null) {
                        dispatch(downloadBlenderProject({ projectId: project.id, versionId: activeVersion.id }));
                      }
                    }}
                    disabled={!project?.id || !activeVersion?.id}
                    className="p-2 flex justify-center items-center rounded text-white hover:text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition-colors"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                </Tooltip>
              )}

              {/* Export documentation button — anchored to the right end of
                  the view-mode row. The longer label (vs. the previous bare
                  "Export") tells the user exactly what comes out of the
                  flow: a PDF/documentation bundle of the active view's
                  outputs. */}
              <div className="ml-auto">
                <Tooltip title={t(exportTooltipKey)}>
                  <button
                    onClick={() => onExportsPanelChange(!showExportsPanel)}
                    disabled={!isExportEnabled}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md hover:bg-white/5 transition-colors disabled:cursor-not-allowed"
                  >
                    <ExportSvg size={15} className={isExportEnabled ? 'text-lime-300' : 'text-gray-500'} />
                    <span className={`text-sm font-medium ${isExportEnabled ? 'text-lime-300' : 'text-gray-500'}`}>
                      {t('workspace.export.documentation')}
                    </span>
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
        )}

        {/* Main Scrollable Content Area */}
        <div
          className={`flex-1 sidebar-content ${showExportsPanel ? 'overflow-y-auto' : 'flex flex-col overflow-hidden'}`}
        >
          {/* Exports Panel - shown when Export button is clicked */}
          {showExportsPanel && isExportEnabled && (
            <div className="h-full flex flex-col" style={{ backgroundColor: greyP4 }}>
              {/* Header with lime-green styling */}
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-2">
                  <ExportSvg size={20} className="text-lime-300" />
                  <span className="text-xl font-medium text-lime-300">{t('workspace.export.title')}</span>
                </div>
              </div>
              {/* Separator line */}
              <div className="border-b border-black" />

              {/* Scrollable Export Sections */}
              <div className="flex-1 overflow-y-auto scrollbar-always-visible px-9 py-4">
                {/* Structure Section */}
                <div>
                  <button
                    className="w-full flex items-center justify-between py-3 text-white hover:text-lime-300 transition-colors"
                    onClick={() => toggleSection('structure')}
                  >
                    <span className={`font-medium text-sm text-[${greyP2}] `}>{t('workspace.structure')}</span>
                    <ChevronRightSmall
                      size={16}
                      className={`transform transition-transform text-[${greyP2}] ${expandedSections.structure ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {expandedSections.structure && renderExportOptions()}
                </div>

                {/* Photometries Section (IDLC-1129 flag; without it the PDF has all of them, as before) */}
                {pdfPhotometrySelection && (
                  <div className="pt-4">
                    <button
                      className="w-full flex items-center justify-between py-3 text-white hover:text-lime-300 transition-colors"
                      onClick={() => toggleSection('photometries')}
                    >
                      <span className={`font-medium text-sm text-[${greyP2}] `}>
                        {t('workspace.export.photometries')}
                      </span>
                      <ChevronRightSmall
                        size={16}
                        className={`transform transition-transform text-[${greyP2}] ${expandedSections.photometries ? 'rotate-90' : ''}`}
                      />
                    </button>
                    {expandedSections.photometries && renderPhotometryExportOptions()}
                  </div>
                )}

                {/* Measurements Section */}
                <div className="pt-4">
                  <button
                    className="w-full flex items-center justify-between py-3 text-white hover:text-lime-300 transition-colors"
                    onClick={() => toggleSection('measurement')}
                  >
                    <span className={`font-medium text-sm text-[${greyP2}] `}>{t('workspace.measurements')}</span>
                    <ChevronRightSmall
                      size={16}
                      className={`transform transition-transform text-[${greyP2}] ${expandedSections.measurement ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {expandedSections.measurement && (
                    <div>
                      {/* Search filter for Measurements */}
                      <div className="relative mb-3">
                        <input
                          type="text"
                          placeholder={t('workspace.searchMeasurements')}
                          value={measurementSearchQuery}
                          onChange={(e) => setMeasurementSearchQuery(e.target.value)}
                          className={`w-full px-3 py-2 pr-8 bg-black text-white text-xs rounded-md outline-none focus:border-lime-400 focus:outline-none placeholder-gray-500 ${
                            measurementSearchQuery ? 'border border-lime-400' : 'border border-gray-700'
                          }`}
                        />
                        {measurementSearchQuery && (
                          <button
                            onClick={() => setMeasurementSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                            title={t('workspace.clearSearch')}
                          >
                            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                      {renderMeasurementExportOptions()}
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom Section - Export Summary Card (Fixed) */}
              <div className="flex-shrink-0">
                <div className={`bg-[${greyP5}] rounded-t-3xl p-8 flex flex-col items-center text-center`}>
                  {/* Title */}
                  <h3 className="text-lime-300 text-3xl font-normal mb-1">
                    {isExportLoading || isGeneratingPdf ? t('workspace.generatingPdf') : t('workspace.exportSummary')}
                  </h3>
                  {/* Subtitle */}
                  <p className="text-white text-lg font-light mb-8">
                    {isExportLoading || isGeneratingPdf
                      ? t('workspace.pleaseWaitPdf')
                      : t('workspace.exportingPages', { count: numPages || 0 })}
                  </p>
                  {/* Download Button */}
                  <button
                    className="w-[300px] bg-lime-400 hover:bg-lime-300 text-black text-xl font-medium rounded-full py-3 px-3 flex items-center shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-lime-400"
                    disabled={!pdfUrl || isExportLoading || isGeneratingPdf}
                    onClick={() => {
                      if (pdfUrl) {
                        const link = document.createElement('a');
                        link.href = pdfUrl;
                        link.download = `${project?.name || 'report'}_export.pdf`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                  >
                    {/* Icon wrapper */}
                    <div className="w-12 h-12 rounded-full border border-black/20 flex items-center justify-center mr-auto bg-lime-300">
                      {isExportLoading || isGeneratingPdf ? (
                        <Loader2 className="animate-spin h-5 w-5" />
                      ) : (
                        <ExportGreen size={20} />
                      )}
                    </div>
                    <span className="flex-1 text-center mr-12">
                      {isExportLoading || isGeneratingPdf ? t('workspace.generatingPdf') : t('workspace.export.title')}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Process Content - Hidden when exports panel is open */}
          {!showExportsPanel && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Model Layers Section */}
              <div
                ref={layersSectionRef}
                className={`border-b border-black flex flex-col ${expandedSections.layers ? 'flex-1 min-h-0' : ''}`}
              >
                <button
                  className={`w-full flex items-center justify-between p-3 transition-colors shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${
                    expandedSections.layers ? 'bg-stone-800 text-lime-400' : 'hover:bg-stone-900 hover:text-lime-500'
                  }`}
                  onClick={() => toggleSection('layers')}
                >
                  <div className="flex items-center">
                    <LayersIcon size={18} className={`mr-2 ${expandedSections.layers ? 'text-lime-400' : ''}`} />
                    <span className={expandedSections.layers ? 'text-lime-400' : ''}>{t('workspace.layers')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Layer count indicator - show to the left of search */}
                    {expandedSections.layers && layerSearchQuery && (
                      <span className="text-xs text-lime-400">
                        {filteredModelLayers.length}/{modelLayers.length}
                      </span>
                    )}
                    {/* Search input - only show when expanded */}
                    {expandedSections.layers && (
                      <div className="relative" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          placeholder={t('workspace.searchLayers')}
                          value={layerSearchQuery}
                          onChange={(e) => setLayerSearchQuery(e.target.value)}
                          className={`w-40 px-3 py-1 pr-7 bg-black text-white text-xs rounded-md outline-none focus:border-lime-400 focus:outline-none placeholder-gray-500 ${
                            layerSearchQuery ? 'border border-lime-400' : 'border border-transparent'
                          }`}
                        />
                        {layerSearchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setLayerSearchQuery('');
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                            title={t('workspace.clearSearch')}
                          >
                            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                    <ChevronRightSmall
                      size={16}
                      className={`ml-2 transform transition-transform ${expandedSections.layers ? 'rotate-90 text-lime-400' : 'text-gray-500'}`}
                    />
                  </div>
                </button>

                {/* Column Headers - only show when expanded */}
                {expandedSections.layers && modelLayers.length > 0 && (
                  <div className="px-2 py-2 flex items-center justify-between border-b border-gray-700 text-xs text-gray-300">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleLayerSort('type')}
                        className="w-6 hover:text-white transition-colors cursor-pointer select-none"
                      >
                        {t('workspace.type')}
                        {layerSortKey === 'type' && <span className="ml-0.5">{layerSortAsc ? '↑' : '↓'}</span>}
                      </button>
                      <button
                        onClick={() => handleLayerSort('name')}
                        className="ml-2 hover:text-white transition-colors cursor-pointer select-none"
                      >
                        {t('workspace.layerName')}
                        {layerSortKey === 'name' && <span className="ml-0.5">{layerSortAsc ? '↑' : '↓'}</span>}
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <Tooltip title={t('workspace.chlTooltip')} arrow placement="top">
                        <button
                          onClick={() => handleLayerSort('chl')}
                          className="w-8 text-center cursor-pointer hover:text-white transition-colors select-none"
                        >
                          {t('workspace.chl')}
                          {layerSortKey === 'chl' && <span className="ml-0.5">{layerSortAsc ? '↑' : '↓'}</span>}
                        </button>
                      </Tooltip>
                      <Tooltip title={t('workspace.lmnTooltip')} arrow placement="top">
                        <button
                          onClick={() => handleLayerSort('lmn')}
                          className="w-8 text-center cursor-pointer hover:text-white transition-colors select-none"
                        >
                          {t('workspace.lmn')}
                          {layerSortKey === 'lmn' && <span className="ml-0.5">{layerSortAsc ? '↑' : '↓'}</span>}
                        </button>
                      </Tooltip>
                      <Tooltip
                        title={t('workspace.normTooltip', 'Lighting standard applied to illuminated layer')}
                        arrow
                        placement="top"
                      >
                        <span className="w-12 text-center cursor-help">{t('workspace.norm')}</span>
                      </Tooltip>
                    </div>
                  </div>
                )}

                <div
                  ref={layerListRef}
                  className={expandedSections.layers ? 'block overflow-y-auto flex-1 min-h-0' : 'hidden'}
                >
                  {/* Mesh Group Tree */}
                  <MeshGroupTree
                    meshGroups={meshGroups}
                    disabled={!isCalculationNotStarted}
                    onAddGroup={(parentGroupId) => {
                      const newGroup: MeshGroup = {
                        uuid: crypto.randomUUID(),
                        name: t('meshGroups.newGroup'),
                        subgroups: [],
                      };
                      addMeshGroup(newGroup, parentGroupId);
                    }}
                    onRenameGroup={renameMeshGroup}
                    onDeleteGroup={deleteMeshGroup}
                    onDropLayerToGroup={(layerUuid, groupId) => {
                      updateLayerGroupId(layerUuid, groupId);
                    }}
                    getLayersForGroup={(groupId) =>
                      filteredModelLayers
                        .filter((l) => (groupId === null ? !l.groupId : l.groupId === groupId))
                        .map((l) => l.uuid)
                    }
                    renderLayerItem={(layerUuid) => {
                      const layer = filteredModelLayers.find((l) => l.uuid === layerUuid);
                      if (!layer) return null;
                      return (
                        <React.Fragment>
                          {renderWorkspaceLayerRow(layer)}
                          {expandedNormLayer === layer.threeJsName && layer.isIlluminated && (
                            <div className="px-4 py-4 border-b border-gray-700" style={{ backgroundColor: greyP4 }}>
                              {layer.customTargets?.length ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    updateLayerCustomTargets(layer.threeJsName, []);
                                  }}
                                  className="mb-3 text-xs text-gray-400 hover:text-white"
                                >
                                  {t('workspace.customTargets.useClass')}
                                </button>
                              ) : (
                                <>
                                  <p className="text-sm text-gray-300 mb-3">{t('workspace.chooseNormForLayer')}</p>
                                  <div className="grid grid-cols-6 gap-1.5 mb-4">
                                    {NORMS.map((norm) => (
                                      <button
                                        key={norm}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNormSelect(layer.threeJsName, norm);
                                        }}
                                        style={{
                                          backgroundColor: layer.selectedNorm === norm ? undefined : greyP5,
                                        }}
                                        className={`h-7 px-1 rounded text-xs font-medium transition ${
                                          norm === LAYER_NORM_NONE ? 'col-span-2' : ''
                                        } ${
                                          layer.selectedNorm === norm
                                            ? 'bg-lime-500 text-black'
                                            : 'text-white hover:bg-lime-500 hover:text-lime-500'
                                        }`}
                                      >
                                        {norm}
                                      </button>
                                    ))}
                                  </div>
                                </>
                              )}
                              <div className="mb-4">
                                <CustomTargetsForm
                                  value={layer.customTargets ?? []}
                                  onApply={(targets) => updateLayerCustomTargets(layer.threeJsName, targets)}
                                />
                              </div>
                              <div className="text-left">
                                <LightingStandardsInfo />
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    }}
                  />
                </div>
              </div>

              {/* Luminaires Section */}
              <div className={`border-b border-black flex flex-col ${expandedSections.lights ? 'flex-1 min-h-0' : ''}`}>
                <button
                  className={`w-full flex items-center justify-between p-3 transition-colors shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${
                    expandedSections.lights ? 'bg-stone-800 text-lime-400' : 'hover:bg-stone-900 hover:text-lime-500'
                  }`}
                  onClick={() => toggleSection('lights')}
                >
                  <div className="flex items-center">
                    <LightingIcon size={18} className={`mr-2 ${expandedSections.lights ? 'text-lime-400' : ''}`} />
                    <span className={expandedSections.lights ? 'text-lime-400' : ''}>{t('workspace.luminaires')}</span>
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    {totalLumenOutput !== null && (
                      <span className="text-[10px] text-gray-300 truncate">
                        {t('workspace.totalLumenOutput')}:{' '}
                        <span className="text-lime-400 font-medium">
                          {Math.round(totalLumenOutput).toLocaleString()} lm
                        </span>
                      </span>
                    )}
                    <ChevronRightSmall
                      size={16}
                      className={`shrink-0 transform transition-transform ${expandedSections.lights ? 'rotate-90 text-lime-400' : 'text-gray-500'}`}
                    />
                  </div>
                </button>

                <div
                  ref={lampListRef}
                  className={expandedSections.lights ? 'block overflow-y-auto flex-1 min-h-0' : 'hidden'}
                >
                  {/* Show message when no layers can hold luminaires AND no lamps are present */}
                  {layersThatCanHoldLuminaires.length === 0 && lamps.length === 0 ? (
                    <div className="px-4 py-4">
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <p className="text-sm text-white leading-relaxed">
                            <Trans
                              i18nKey="workspace.autopositionLayersHint"
                              components={[<span key="0" className="text-lime-400" />]}
                            />
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <AutopositionIcon />
                        </div>
                      </div>
                      <div className="mt-4 text-center">
                        <p className="text-sm text-gray-300 mb-3">{t('workspace.noLayersSelected')}</p>
                        <div className="flex flex-col gap-2 items-center">
                          <button
                            onClick={() => {
                              openSection('layers');
                              requestAnimationFrame(() => {
                                layersSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              });
                              // Flash orange borders on CHL/Lmn toggles
                              if (highlightTimeoutRef.current !== null) {
                                clearTimeout(highlightTimeoutRef.current);
                                highlightTimeoutRef.current = null;
                              }
                              setHighlightToggles(true);
                              highlightTimeoutRef.current = setTimeout(() => {
                                setHighlightToggles(false);
                                highlightTimeoutRef.current = null;
                              }, 1500);
                            }}
                            className="px-6 py-2 text-sm bg-lime-700 hover:bg-lime-600 text-white rounded-md transition-colors w-full max-w-[260px]"
                          >
                            {t('workspace.setUpLayerParameters')}
                          </button>

                          {/* Add Luminaire Manually Button - also shown here when no layers are selected */}
                          {isCalculationNotStarted && !isAutopositioningInProgress && (
                            <AddLuminaireButton
                              onClick={handleInstantAddLamp}
                              variant="md"
                              className="w-full max-w-[200px]"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Layers that can hold luminaires section - only shown before lamps are added */}
                      {lamps.length === 0 && (
                        <div className="border-b border-gray-700">
                          <div className="px-4 py-2">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs text-gray-300">
                                {t('workspace.canHoldLightLayers', 'Can hold light layers')}
                              </p>
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-gray-300">
                                  {t('workspace.defaultHeightAutomatic', { unit: lengthUnitLabel })}
                                </span>
                                <NumericInput
                                  min={heightInputMin}
                                  max={heightInputMax}
                                  step={heightInputStep}
                                  value={parseFloat(luminaireHeightInput) || heightInputMin}
                                  decimals={2}
                                  onValueChange={(v) => {
                                    setLuminaireHeightInput(String(v));
                                    const heightInMeters = fromDisplayLength(v, unitSystem);
                                    if (
                                      !isNaN(heightInMeters) &&
                                      heightInMeters >= MIN_LUMINAIRE_HEIGHT_METERS &&
                                      heightInMeters <= MAX_LUMINAIRE_HEIGHT_METERS
                                    ) {
                                      setDesiredLuminaireHeight(heightInMeters);
                                      layersThatCanHoldLuminaires.forEach((layer) => {
                                        updateLayer(layer.uuid, { desiredLuminaireHeight: heightInMeters });
                                      });
                                    }
                                  }}
                                  onCommit={(v) => {
                                    const clampedDisplay = Math.min(Math.max(v, heightInputMin), heightInputMax);
                                    setLuminaireHeightInput(clampedDisplay.toFixed(2));
                                    const heightInMeters = fromDisplayLength(clampedDisplay, unitSystem);
                                    const safe = Math.min(
                                      Math.max(heightInMeters, MIN_LUMINAIRE_HEIGHT_METERS),
                                      MAX_LUMINAIRE_HEIGHT_METERS,
                                    );
                                    setDesiredLuminaireHeight(safe);
                                    layersThatCanHoldLuminaires.forEach((layer) => {
                                      updateLayer(layer.uuid, { desiredLuminaireHeight: safe });
                                    });
                                  }}
                                  className={`w-14 px-1 py-0.5 border border-gray-600 rounded text-white text-center text-xs focus:outline-none focus:ring-1 focus:ring-lime-500 bg-black ${highlightHeights ? 'animate-highlight-toggle' : ''}`}
                                  disabled={!isCalculationNotStarted}
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              {layersThatCanHoldLuminaires.map((layer) => (
                                <div
                                  key={layer.uuid}
                                  className="flex items-center gap-2 py-1.5 px-2 bg-stone-800 rounded text-sm"
                                  onMouseEnter={() => setHoveredLayer(layer.threeJsName)}
                                  onMouseLeave={() => setHoveredLayer(null)}
                                >
                                  <LayerTypeIcon
                                    type={layer.layerType}
                                    style={{ width: 14, height: 14, color: 'white' }}
                                  />
                                  <div
                                    className="w-3 h-3 rounded border border-gray-400"
                                    style={{ backgroundColor: layer.color || '#666' }}
                                  />
                                  <span className="text-white truncate flex-1">{layer.displayName}</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-300">H:</span>
                                    <NumericInput
                                      min={heightInputMin}
                                      step={heightInputStep}
                                      decimals={2}
                                      value={toDisplayLength(
                                        layer.desiredLuminaireHeight ?? desiredLuminaireHeight ?? 8,
                                        unitSystem,
                                      )}
                                      onValueChange={(v) => handleLayerHeightChange(layer.uuid, v)}
                                      className={`w-12 px-1 py-0.5 border border-gray-600 rounded text-white text-center text-xs focus:outline-none focus:ring-1 focus:ring-lime-500 bg-black ${highlightHeights ? 'animate-highlight-toggle' : ''}`}
                                      disabled={!isCalculationNotStarted}
                                    />
                                    <span className="text-xs text-gray-300">{lengthUnitLabel}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Luminaires list */}
                      {lamps.length === 0 ? (
                        <div className="px-4 py-3 text-center">
                          <div className="text-sm text-gray-300">{t('workspace.noLuminairesYet')}</div>
                          {/* Add Luminaire Manually Button - shown when no lamps and results don't exist */}
                          {isCalculationNotStarted && !isAutopositioningInProgress && (
                            <div className="flex justify-center py-3 mt-2">
                              <AddLuminaireButton onClick={handleInstantAddLamp} disabled={isMaxLampsReached} />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1 px-2 py-2">
                          <div className="px-2 pb-1 text-[10px] text-gray-300 italic">
                            {t('workspace.coordinatesInUnit', { unit: lengthUnitLabel })}
                          </div>
                          {sortedLamps.map((lamp) => {
                            const handleCoordinateChange = (axis: 'x' | 'y' | 'z', value: string) => {
                              const numValue = parseFloat(value);
                              const valueInMeters = fromDisplayLength(numValue, unitSystem);
                              if (!isNaN(valueInMeters)) {
                                const newPosition = [...lamp.position] as [number, number, number];
                                const axisIndex = axis === 'x' ? 0 : axis === 'y' ? 1 : 2;

                                newPosition[axisIndex] = valueInMeters;
                                updateLampPosition(lamp.id, newPosition);
                              }
                            };

                            const handleHeightChange = (value: string) => {
                              const numValue = parseFloat(value);
                              const valueInMeters = fromDisplayLength(numValue, unitSystem);
                              if (isNaN(valueInMeters)) return;
                              const clamped = Math.min(
                                Math.max(valueInMeters, MIN_LUMINAIRE_HEIGHT_METERS),
                                MAX_LUMINAIRE_HEIGHT_METERS,
                              );
                              const heightDiff = clamped - (lamp.distanceToGround || 0);
                              const newPosition = [...lamp.position] as [number, number, number];
                              newPosition[2] += heightDiff;
                              updateLampPosition(lamp.id, newPosition);
                              updateLampDistance(lamp.id, clamped);
                            };

                            return (
                              <div
                                key={lamp.id}
                                data-lamp-id={lamp.id}
                                className={`group flex flex-col gap-1 py-1.5 px-2 rounded transition-colors ${
                                  lamp.isSelected ? 'bg-lime-400/20 ring-1 ring-lime-400/60' : 'hover:bg-stone-800'
                                }`}
                                onClick={() => handleLampClick(lamp.id)}
                              >
                                {/* Main row with coordinates */}
                                <div className="flex items-center gap-1">
                                  {/* Lamp icon and name */}
                                  <Tooltip
                                    title={
                                      lamp.distanceToGround === DISTANCE_TO_GROUND_NA
                                        ? t('workspace.lampNotAboveModel')
                                        : ''
                                    }
                                  >
                                    <Lightbulb
                                      size={14}
                                      className={`shrink-0 ${
                                        lamp.distanceToGround === DISTANCE_TO_GROUND_NA
                                          ? 'text-red-400 animate-pulse'
                                          : lamp.variantId != null
                                            ? 'text-lime-400'
                                            : 'text-yellow-400'
                                      }`}
                                    />
                                  </Tooltip>
                                  {editingLampId === lamp.id ? (
                                    <input
                                      autoFocus
                                      type="text"
                                      value={editingLampName}
                                      onChange={(e) => setEditingLampName(e.target.value)}
                                      onBlur={() => {
                                        if (editingLampName.trim()) {
                                          updateLampName(lamp.id, editingLampName.trim());
                                        }
                                        setEditingLampId(null);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          if (editingLampName.trim()) {
                                            updateLampName(lamp.id, editingLampName.trim());
                                          }
                                          setEditingLampId(null);
                                        } else if (e.key === 'Escape') {
                                          setEditingLampId(null);
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="text-xs bg-black text-white border border-lime-500 rounded px-1 w-16 outline-none"
                                    />
                                  ) : (
                                    <span
                                      className="text-xs text-white truncate w-16 cursor-text"
                                      title={lamp.name}
                                      onDoubleClick={(e) => {
                                        if (!isCalculationNotStarted) return;
                                        e.stopPropagation();
                                        setEditingLampId(lamp.id);
                                        setEditingLampName(lamp.name || '');
                                      }}
                                    >
                                      {lamp.name}
                                    </span>
                                  )}

                                  {/* X coordinate */}
                                  <div className="flex items-center gap-0.5">
                                    <span className="text-[10px] text-gray-300">X</span>
                                    <NumericInput
                                      value={toDisplayLength(lamp.position[0], unitSystem)}
                                      decimals={2}
                                      onValueChange={(v) => handleCoordinateChange('x', String(v))}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`w-12 bg-black text-white px-1 py-0.5 rounded text-xs text-center ${!isCalculationNotStarted ? 'opacity-70 cursor-not-allowed' : ''}`}
                                      step="0.1"
                                      readOnly={!isCalculationNotStarted}
                                    />
                                  </div>

                                  {/* Y coordinate */}
                                  <div className="flex items-center gap-0.5">
                                    <span className="text-[10px] text-gray-300">Y</span>
                                    <NumericInput
                                      value={toDisplayLength(lamp.position[1], unitSystem)}
                                      decimals={2}
                                      onValueChange={(v) => handleCoordinateChange('y', String(v))}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`w-12 bg-black text-white px-1 py-0.5 rounded text-xs text-center ${!isCalculationNotStarted ? 'opacity-70 cursor-not-allowed' : ''}`}
                                      step="0.1"
                                      readOnly={!isCalculationNotStarted}
                                    />
                                  </div>

                                  {/* Z coordinate */}
                                  <div className="flex items-center gap-0.5">
                                    <span className="text-[10px] text-gray-300">Z</span>
                                    <NumericInput
                                      value={toDisplayLength(lamp.position[2], unitSystem)}
                                      decimals={2}
                                      onValueChange={(v) => handleCoordinateChange('z', String(v))}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`w-12 bg-black text-white px-1 py-0.5 rounded text-xs text-center ${!isCalculationNotStarted ? 'opacity-70 cursor-not-allowed' : ''}`}
                                      step="0.1"
                                      readOnly={!isCalculationNotStarted}
                                    />
                                  </div>

                                  {/* Height */}
                                  <div className="flex items-center gap-0.5">
                                    <span className="text-[10px] text-gray-300">H</span>
                                    {lamp.distanceToGround === DISTANCE_TO_GROUND_NA ? (
                                      <div
                                        className="w-12 bg-black text-yellow-400 px-1 py-0.5 rounded text-[10px] text-center border border-orange-500"
                                        title="Lamp must be above the model"
                                      >
                                        N/A
                                      </div>
                                    ) : (
                                      <NumericInput
                                        value={toDisplayLength(lamp.distanceToGround ?? 0, unitSystem)}
                                        decimals={2}
                                        onValueChange={(v) => handleHeightChange(String(v))}
                                        onClick={(e) => e.stopPropagation()}
                                        className={`w-12 bg-black text-white px-1 py-0.5 rounded text-xs text-center ${!isCalculationNotStarted ? 'opacity-70 cursor-not-allowed' : ''}`}
                                        step="0.1"
                                        readOnly={!isCalculationNotStarted}
                                      />
                                    )}
                                  </div>

                                  {/* Download IES button - show when calculation is completed and iesFileId exists */}
                                  {isCalculationCompleted && lamp.iesFileId && (
                                    <Tooltip title={t('workspace.downloadIesFile')}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          dispatch(downloadIesFile(lamp.iesFileId!));
                                        }}
                                        className="text-gray-400 hover:text-lime-400 p-0.5 transition-colors"
                                      >
                                        <Download size={14} />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* 3D preview button - show when calculation is completed and iesFileId exists */}
                                  {isCalculationCompleted && lamp.iesFileId && (
                                    <Tooltip title={t('workspace.preview3dIes')}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpen3dPreview(lamp.iesFileId!, lamp.name || '');
                                        }}
                                        className="text-gray-400 hover:text-lime-400 p-0.5 transition-colors"
                                      >
                                        <Eye size={14} />
                                      </button>
                                    </Tooltip>
                                  )}

                                  {/* Action icons - visible on hover or when selected */}
                                  <div
                                    className={`flex items-center gap-1 ml-auto ${lamp.isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
                                  >
                                    {/* Find/Target icon */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!lamp.isSelected) {
                                          handleLampClick(lamp.id);
                                        }
                                        const [x, y, z] = lamp.position;
                                        setTargetCameraPosition({
                                          position: [x, y, z + 20],
                                          lookAt: [x, y, z],
                                        });
                                      }}
                                      className="text-gray-400 hover:text-lime-400 p-0.5 transition-colors"
                                      title={t('workspace.findLuminaire')}
                                    >
                                      <Crosshair size={14} />
                                    </button>

                                    {/* Duplicate icon */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const {
                                          id: _id,
                                          uuid: _uuid,
                                          isSelected: _isSelected,
                                          lumenOutput: _lumenOutput,
                                          ...lampWithoutId
                                        } = lamp;
                                        addLamp({
                                          ...lampWithoutId,
                                          uuid: crypto.randomUUID(),
                                          name: `${lampWithoutId.name} (copy)`,
                                        });
                                      }}
                                      className={`text-gray-400 hover:text-lime-400 p-0.5 transition-colors ${!isCalculationNotStarted ? 'opacity-70 cursor-not-allowed' : ''}`}
                                      disabled={!isCalculationNotStarted}
                                      title={t('workspace.duplicateLuminaire')}
                                    >
                                      <Copy size={14} />
                                    </button>

                                    {/* Delete icon */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeLamp(lamp.id);
                                      }}
                                      className={`text-gray-400 hover:text-red-400 p-0.5 transition-colors ${!isCalculationNotStarted ? 'opacity-70 cursor-not-allowed' : ''}`}
                                      disabled={!isCalculationNotStarted}
                                      title={t('workspace.deleteLuminaire')}
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                </div>

                                {/* Second row: Lumen output - show when calculation is completed */}
                                {isCalculationCompleted && lamp.lumenOutput !== undefined && (
                                  <div className="flex items-center gap-1 ml-5 text-[10px]">
                                    <span className="text-gray-300">{t('workspace.lumenOutput')}:</span>
                                    <span className="text-lime-400 font-medium">
                                      {Math.round(lamp.lumenOutput).toLocaleString()} lm
                                    </span>
                                  </div>
                                )}

                                {/* Catalogue variant badge — shown when a product variant is assigned */}
                                {lamp.variantId != null &&
                                  lamp.productId != null &&
                                  (() => {
                                    const detail = lampSimilarityDetails.find((d) => d.lampId === lamp.productId);
                                    const cacheKey = `${lamp.productId}-${lamp.variantId}`;
                                    const label =
                                      catalogueLabels[cacheKey] ?? detail?.name ?? `Product #${lamp.productId}`;
                                    const catalogueUrl = buildCatalogueUrl({
                                      productId: lamp.productId,
                                      seriesId: lamp.seriesId,
                                      manufacturerId: lamp.manufacturerId,
                                      variantId: lamp.variantId,
                                    });
                                    return (
                                      <div className="flex items-center gap-1 ml-5 text-[10px]">
                                        <Box size={10} className="text-lime-400 shrink-0" />
                                        <a
                                          href={catalogueUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={label}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-lime-400 hover:underline truncate max-w-[120px]"
                                        >
                                          {label}
                                        </a>
                                        {isCalculationNotStarted && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setProductPickerLampId(lamp.id);
                                            }}
                                            title={t('workspace.changeProduct')}
                                            className="ml-auto text-gray-400 hover:text-lime-400 transition-colors p-0.5 opacity-0 group-hover:opacity-100"
                                          >
                                            <Pencil size={10} />
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}

                                {/* Assign product button — shown on market-matched versions for lamps without an assignment */}
                                {isMarketMatched && lamp.variantId == null && isCalculationNotStarted && (
                                  <div className="flex items-center gap-1 ml-5 text-[10px]">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setProductPickerLampId(lamp.id);
                                      }}
                                      className="flex items-center gap-1 text-gray-300 hover:text-lime-400 transition-colors"
                                    >
                                      <Box size={10} className="shrink-0" />
                                      <span>{t('workspace.assignProduct')}</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {isCalculationNotStarted && !isAutopositioningInProgress && (
                            <div className="flex justify-center py-3 mt-2">
                              <AddLuminaireButton onClick={handleInstantAddLamp} disabled={isMaxLampsReached} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Results Section - shown after ML calculation or frontend calculation */}
              {(isCalculationCompleted ||
                fullyAssigned ||
                appliedLampSimilaritySelection?.previewMode === 'frontend') &&
                measurementData &&
                measurementData.length > 0 && (
                  <div
                    className={`border-b border-black flex flex-col ${expandedSections.results ? 'flex-1 min-h-0' : ''}`}
                  >
                    <button
                      className={`w-full flex items-center justify-between p-3 transition-colors shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${
                        expandedSections.results
                          ? 'bg-stone-800 text-lime-400'
                          : 'hover:bg-stone-900 hover:text-lime-500'
                      }`}
                      onClick={() => toggleSection('results')}
                    >
                      <div className="flex items-center">
                        <ResultsIcon size={18} className={`mr-2 ${expandedSections.results ? 'text-lime-400' : ''}`} />
                        <span className={expandedSections.results ? 'text-lime-400' : ''}>
                          {t('workspace.results')}
                        </span>
                      </div>
                      <ChevronRightSmall
                        size={16}
                        className={`ml-2 transform transition-transform ${expandedSections.results ? 'rotate-90 text-lime-400' : 'text-gray-500'}`}
                      />
                    </button>

                    <div className={expandedSections.results ? 'block overflow-y-auto flex-1 min-h-0' : 'hidden'}>
                      {renderResultsContent()}
                    </div>
                  </div>
                )}

              {/* Measurement surfaces — user-drawn calculation surfaces (frontend-only, flag-gated).
                  Only available once there are measurement results to draw onto. */}
              {measurementSurfacesEnabled && isMeasurementDataAvailable && <MeasurementSurfacesPanel />}

              {/* Market Matches Section - only after ML calculation; hidden on market-matched versions (no hybrid workflow) */}
              {isCalculationCompleted && !isMarketMatched && (
                <div
                  className={`border-b border-black flex flex-col ${expandedSections.marketMatches ? 'flex-1 min-h-0' : ''}`}
                >
                  <div
                    className={`w-full flex items-center justify-between p-3 transition-colors shadow-[0_2px_4px_rgba(0,0,0,0.3)] ${
                      expandedSections.marketMatches
                        ? 'bg-stone-800 text-lime-400'
                        : 'hover:bg-stone-900 hover:text-lime-500'
                    }`}
                  >
                    <button
                      className="flex items-center flex-1"
                      onClick={() => {
                        if (!expandedSections.marketMatches && lampSimilarityCandidates.length > 0) {
                          dispatch(loadLampSimilarityDetails());
                        }
                        toggleSection('marketMatches');
                      }}
                    >
                      <MarketMatchToolbar
                        width={18}
                        height={21}
                        className={`mr-2 ${expandedSections.marketMatches ? 'text-lime-400' : ''}`}
                      />
                      <span className={expandedSections.marketMatches ? 'text-lime-400' : ''}>
                        {t('workspace.marketMatches')}
                      </span>
                      {isLampSimilarityFailed && (
                        <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-red-500/20 text-red-400 rounded uppercase tracking-wider">
                          {t('common.failed')}
                        </span>
                      )}
                    </button>
                    <ChevronRightSmall
                      size={16}
                      className={`ml-2 transform transition-transform ${
                        expandedSections.marketMatches ? 'rotate-90 text-lime-400' : 'text-gray-500'
                      }`}
                    />
                  </div>

                  <div className={expandedSections.marketMatches ? 'block overflow-y-auto flex-1 min-h-0' : 'hidden'}>
                    {renderMarketMatchesContent()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Section - Optimization Button - Hidden when exports panel is open, project fully
            completed, or on market-matched versions (no ML optimization / hybrid workflow) */}
        {!showExportsPanel &&
          !isMarketMatched &&
          !(isCalculationCompleted && (isBakingJobCompleted || isBakingJobFailed)) && (
            <div className="mt-auto min-h-[200px]">
              {/* Optimization Section - Conditional based on state */}
              {optimizationState === 'not_ready' ? (
                // State 2.1 - Not ready (Disabled)
                <div className="h-full relative overflow-hidden bg-greyP5 rounded-t-xl p-6 text-center items-center">
                  {/* Background TAMAN Logo - subtle and transparent */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
                    <TamanLogo className="w-[30rem] h-auto" />
                  </div>

                  {/* Content */}
                  <h2 className="text-white text-xl font-normal relative z-10">
                    {t('workspace.optimizationNotAvailable', {
                      mode: lamps.length > 0 ? t('workspace.optimization') : t('workspace.autoposition'),
                    })}
                  </h2>
                  <p className="text-greyP2 text-sm mt-1 relative z-10">
                    {((lamps.length > 0 && illuminatedLayers.length > 0) ||
                      (layersThatCanHoldLuminaires.length > 0 && illuminatedLayers.length > 0)) &&
                    !illuminatedLayers.every(
                      (layer) =>
                        layer.selectedNorm && layer.selectedNorm !== '' && layer.selectedNorm !== LAYER_NORM_NONE,
                    ) ? (
                      <Trans i18nKey="workspace.selectNormHint" components={[<span className="text-white" />]} />
                    ) : (
                      <Trans
                        i18nKey={lamps.length > 0 ? 'workspace.minSelectionHintLamps' : 'workspace.minSelectionHint'}
                        values={{ mode: lamps.length > 0 ? t('workspace.optimization') : t('workspace.autoposition') }}
                        components={[<span className="text-white" />, <span className="text-white" />]}
                      />
                    )}
                  </p>
                  {lamps.length > 0 && (
                    <div className="relative z-10 mt-4 mx-auto w-[300px] text-left">
                      <MaxAngleControl />
                    </div>
                  )}
                  <button
                    disabled
                    className="w-[300px] h-[50px] bg-greyP3 text-greyP4 rounded-full px-3 py-3 mt-6 m-auto flex items-center justify-between cursor-not-allowed relative z-10"
                  >
                    {/* Icon wrapper */}
                    <div className="w-8 h-8 rounded-full border border-[#6A6969] flex items-center justify-center mr-3">
                      {lamps.length > 0 ? <Loader2 size={18} /> : <Autoposition size={18} />}
                    </div>
                    <span className="font-medium">
                      {lamps.length > 0 ? t('workspace.optimization') : t('workspace.autoposition')}
                    </span>
                    <div></div>
                  </button>
                </div>
              ) : optimizationState === 'ready_for_autoposition' ? (
                // State 2.2 - Ready for autoposition (Enabled)
                <div
                  className="h-full relative overflow-hidden bg-black rounded-t-3xl p-6 text-center"
                  style={{ background: 'linear-gradient(to bottom, #000000 0%, rgba(249, 253, 99, 0.12) 100%)' }}
                >
                  {/* Background TAMAN Logo - slightly more visible with green tint */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                    <TamanLogo className="w-[30rem] h-auto" />
                  </div>

                  {/* Content */}
                  <h2 className="text-lime-300 text-xl font-normal relative z-10">
                    {t('workspace.allSetAutoposition')}
                  </h2>
                  <p className="text-white text-sm mt-1 relative z-10">
                    <Trans
                      i18nKey="workspace.autopositionDescription"
                      values={{
                        chlCount: layersThatCanHoldLuminaires.length,
                        lmnCount: illuminatedLayers.length,
                      }}
                      components={[<span className="text-lime-300" />, <span className="text-lime-300" />]}
                    />
                  </p>

                  {/* CHL Heights summary */}
                  {layersThatCanHoldLuminaires.length > 0 && (
                    <div className="relative z-10 mt-3 text-[11px] text-white leading-relaxed">
                      <span className="text-gray-300">
                        {t('workspace.chl')} {t('workspace.heights', 'Heights')}:{' '}
                      </span>
                      {layersThatCanHoldLuminaires.map((layer, i) => (
                        <span key={layer.uuid}>
                          {layer.displayName}{' '}
                          <span className="text-lime-400">
                            (
                            {toDisplayLength(
                              layer.desiredLuminaireHeight ?? desiredLuminaireHeight ?? 8,
                              unitSystem,
                            ).toFixed(2)}{' '}
                            {lengthUnitLabel})
                          </span>
                          {i < layersThatCanHoldLuminaires.length - 1 && ', '}
                        </span>
                      ))}
                      <button
                        onClick={() => {
                          openSection('lights');
                          requestAnimationFrame(() => {
                            lampListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          });
                          if (highlightTimeoutRef.current !== null) {
                            clearTimeout(highlightTimeoutRef.current);
                            highlightTimeoutRef.current = null;
                          }
                          setHighlightHeights(true);
                          highlightTimeoutRef.current = setTimeout(() => {
                            setHighlightHeights(false);
                            highlightTimeoutRef.current = null;
                          }, 1500);
                        }}
                        className="ml-1 inline-flex items-center gap-0.5 text-lime-400 hover:text-lime-300 transition-colors"
                      >
                        {t('workspace.editHeights', 'Edit heights')}
                        <ArrowUp size={10} />
                      </button>
                    </div>
                  )}

                  {isAutopositioningInProgress || isCancelingAutoPositioning ? (
                    <div className="w-[300px] h-[50px] mt-4 m-auto flex items-center bg-black rounded-full shadow-lg relative z-10 overflow-hidden p-1">
                      {/* Progress / status area (left) */}
                      <div className="relative flex-1 h-full rounded-full overflow-hidden bg-black">
                        {/* Animated indeterminate fill */}
                        <div
                          className={`absolute inset-y-0 left-0 right-0 bg-lime-400 ${
                            isCancelingAutoPositioning ? 'opacity-40' : 'animate-pulse'
                          }`}
                        />
                        <div className="relative h-full flex items-center px-2 text-black font-medium">
                          <div className="w-8 h-8 rounded-full border border-black/20 bg-lime-300 flex items-center justify-center mr-2 shrink-0">
                            {isCancelingAutoPositioning ? (
                              <Loader2 size={18} className="animate-spin" />
                            ) : (
                              <Autoposition size={18} />
                            )}
                          </div>
                          <span className="text-sm truncate">
                            {isCancelingAutoPositioning
                              ? t('workspace.cancellingAutopositioning')
                              : t('workspace.autopositioning')}
                          </span>
                        </div>
                      </div>
                      {/* Fixed Cancel button (right) */}
                      <button
                        onClick={cancelAutoPositioning}
                        disabled={isCancelingAutoPositioning}
                        title={t('workspace.cancelAutopositioning')}
                        style={{ backgroundColor: orangeError, borderColor: orangeError }}
                        className={`ml-1 h-[42px] w-[96px] shrink-0 rounded-full flex items-center justify-center gap-1 text-white border-2 transition-colors ${
                          isCancelingAutoPositioning
                            ? 'opacity-60 cursor-not-allowed'
                            : 'hover:brightness-110 cursor-pointer'
                        }`}
                      >
                        <X size={16} strokeWidth={2.5} />
                        <span className="text-sm font-medium">{t('common.cancel', 'Cancel')}</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleStartAutoPositioning}
                      disabled={!isCalculationNotStarted || isAutoPositioningStarting}
                      className={`group w-[300px] h-[50px] font-medium rounded-full px-3 py-3 mt-4 m-auto flex items-center justify-between shadow-lg transition-colors relative z-10 ${
                        !isCalculationNotStarted || isAutoPositioningStarting
                          ? 'bg-lime-400 text-black opacity-50 cursor-not-allowed'
                          : 'bg-lime-400 hover:bg-lime-300 text-black cursor-pointer'
                      }`}
                    >
                      {/* Icon wrapper */}
                      <div className="w-8 h-8 rounded-full border border-black/20 bg-lime-300 flex items-center justify-center mr-3 transition-colors">
                        {isAutoPositioningStarting ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Autoposition size={18} />
                        )}
                      </div>
                      <span>
                        {isAutoPositioningStarting
                          ? t('workspace.preparing')
                          : isCalculationCompleted
                            ? t('workspace.completed')
                            : t('workspace.autoposition')}
                      </span>
                      <div></div>
                    </button>
                  )}

                  {/* Add luminaire manually */}
                  {isCalculationNotStarted && !isAutopositioningInProgress && (
                    <div className="relative z-10 mt-3 flex justify-center" style={{ transform: 'scale(0.8)' }}>
                      <AddLuminaireButton onClick={handleInstantAddLamp} disabled={isMaxLampsReached} />
                    </div>
                  )}
                </div>
              ) : hasLampsNotAboveModel || isMaxLampsReached ? (
                // State 2.3a - Lamps not above model or max lamps reached (grey/disabled like not_ready)
                <div className="h-full relative overflow-hidden bg-greyP5 rounded-t-xl p-6 text-center items-center">
                  {/* Background TAMAN Logo - subtle and transparent */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none">
                    <TamanLogo className="w-[30rem] h-auto" />
                  </div>

                  {/* Content */}
                  <h2 className="text-white text-xl font-normal relative z-10">{sidebarTitle}</h2>
                  <p className="text-greyP2 text-sm mt-1 relative z-10">{sidebarDescription}</p>
                  <div className="relative z-10 mt-4 mx-auto w-[300px] text-left">
                    <MaxAngleControl />
                  </div>
                  <button
                    disabled
                    className="w-[300px] h-[50px] bg-greyP3 text-greyP4 rounded-full px-3 py-3 mt-6 m-auto flex items-center justify-between cursor-not-allowed relative z-10"
                  >
                    {/* Icon wrapper */}
                    <div className="w-8 h-8 rounded-full border border-[#6A6969] flex items-center justify-center mr-3">
                      <Loader2 size={18} />
                    </div>
                    <span className="font-medium">{t('workspace.optimization')}</span>
                    <div></div>
                  </button>
                </div>
              ) : (
                // State 2.3 - Ready for optimization (luminaires added) or optimization in progress
                <div
                  className="h-full relative overflow-hidden bg-black rounded-t-3xl p-6 text-center"
                  style={{ background: 'linear-gradient(to bottom, #000000 0%, rgba(249, 253, 99, 0.12) 100%)' }}
                >
                  {/* Background TAMAN Logo */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
                    <TamanLogo className="w-[30rem] h-auto" />
                  </div>

                  {/* Content */}
                  <h2 className="text-lime-300 text-xl font-normal relative z-10">{sidebarTitle}</h2>
                  <p className="text-white text-sm mt-1 relative z-10">{sidebarDescription}</p>
                  <div className="relative z-10 mt-4 mx-auto w-[300px] text-left">
                    <MaxAngleControl />
                  </div>
                  <button
                    onClick={handleTamanOptimisation}
                    disabled={
                      isCalculationInProgress ||
                      isRenderBakingInProgress ||
                      !isCalculationNotStarted ||
                      isOptimizationStarting
                    }
                    className={`w-[300px] h-[50px] bg-lime-400 hover:bg-lime-300 text-black font-medium rounded-full px-3 py-3 mt-6 m-auto flex items-center justify-center shadow-lg transition-colors cursor-pointer relative z-10 ${
                      isCalculationInProgress ||
                      isRenderBakingInProgress ||
                      !isCalculationNotStarted ||
                      isOptimizationStarting
                        ? 'opacity-50 cursor-not-allowed hover:bg-lime-400'
                        : ''
                    }`}
                  >
                    {/* Icon wrapper */}
                    {(isCalculationInProgress || isRenderBakingInProgress || isOptimizationStarting) && (
                      <div className="w-8 h-8 rounded-full border border-black/20 flex items-center justify-center mr-3 bg-lime-300">
                        <Loader2 size={18} className="animate-spin" />
                      </div>
                    )}
                    <span>{sidebarButtonText}</span>
                  </button>
                </div>
              )}
            </div>
          )}
      </div>

      {/* Layer Type Popup Dropdown - rendered outside scroll container */}
      {expandedTypeLayer && (
        <div className="fixed inset-0 z-50" onClick={() => setExpandedTypeLayer(null)}>
          <div
            className="fixed rounded-md overflow-hidden min-w-[180px] z-50 border border-gray-600"
            style={{
              top: typeDropdownPosition.top,
              left: typeDropdownPosition.left,
              backgroundColor: greyP5,
              boxShadow: '4px 6px 16px 0px rgba(0, 0, 0, 0.6)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Dropdown Title */}
            <div className="px-3 py-2" style={{ backgroundColor: greyP4 }}>
              <span className="text-xs font-medium text-gray-300">Set layer type</span>
            </div>

            {/* Layer Type Options */}
            <div className="py-1">
              {layerTypeKeys(
                pedestrianLayerType,
                modelLayers.find((l) => l.threeJsName === expandedTypeLayer)?.layerType,
              ).map((type) => {
                const currentLayer = modelLayers.find((l) => l.threeJsName === expandedTypeLayer);
                return (
                  <button
                    key={type}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect(expandedTypeLayer, type as keyof typeof LAYER_TYPES);
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left transition ${
                      currentLayer?.layerType === type
                        ? 'bg-green02 text-black'
                        : 'text-white hover:bg-green01 hover:text-black'
                    }`}
                  >
                    <LayerTypeIcon
                      type={type as keyof typeof LAYER_TYPES}
                      style={{ width: 14, height: 14, fill: 'currentColor' }}
                    />
                    <span className="text-sm">{layerTypeLabel(t, type)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <MarketMatchResultsPreviewModal
        isOpen={!!marketMatchResultsPreview}
        lampName={marketMatchResultsPreview?.lampName || ''}
        aggregateResults={marketMatchResultsPreview?.aggregateResults || []}
        layerDisplayNames={layerDisplayNames}
        norms={norms}
        onClose={() => setMarketMatchResultsPreview(null)}
      />

      {productPickerLampId != null && (
        <ProductVariantPicker
          initialSelection={(() => {
            const lamp = lamps.find((l) => l.id === productPickerLampId);
            if (
              lamp?.productId != null &&
              lamp.variantId != null &&
              lamp.seriesId != null &&
              lamp.manufacturerId != null
            ) {
              return {
                manufacturerId: lamp.manufacturerId,
                seriesId: lamp.seriesId,
                productId: lamp.productId,
                variantId: lamp.variantId,
              };
            }
            return null;
          })()}
          onSelect={(selection: ProductVariantSelection) => {
            dispatch(
              updateLampProductVariant({
                id: productPickerLampId,
                productId: selection.productId,
                variantId: selection.variantId,
                seriesId: selection.seriesId,
                manufacturerId: selection.manufacturerId,
              }),
            );
            setProductPickerLampId(null);
          }}
          onClose={() => setProductPickerLampId(null)}
        />
      )}

      <IesPreview3DModal
        isOpen={ies3dPreview.isOpen}
        onClose={() => {
          latestIesPreviewRequestIdRef.current += 1;
          setIes3dPreview({ isOpen: false, lampName: '' });
        }}
        iesUrl={ies3dPreview.iesUrl}
        title={ies3dPreview.lampName}
      />
    </>
  );
}
